import './CampaignAudit.scss';
import cn from 'classnames';
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Form } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Appearance, Placement, Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv, InteractiveSpan } from '@Components/InteractiveEl';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { Page, PageNav } from '@Components/Page';
import { Spinner } from '@Components/Spinner';
import { useNavigate } from 'react-router-dom';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useAppSelector } from '@src/redux';
import * as UserRedux from '@src/redux/user';
import { useFilteredList } from '@src/utils/search';
import { useSetError } from '@src/redux/modals';
import { isSafeHttpUrl } from '@src/utils/url-utils';
import {
  ACTIONS_KEY,
  AUDIT_INTAKE_FORM_URL,
  AUDIT_ROLE_ORDER,
  AuditRoleLabel,
  AuditRoleType,
  CATEGORY_FRAME,
  CATEGORY_ORDER,
  AuditCategoryShortLabel,
  DoctrineClass,
  DoctrineLabel,
  FUNNEL_SIGNAL_IDS,
  GUIDANCE_KEY,
  HOWTO_KEY,
  OVERVIEW_KEY,
  QUESTIONS_KEY,
  ROLES_BY_CATEGORY,
  VERDICT_OPTIONS,
  VerdictAbbrev,
  VerdictClass,
  VerdictLabel
} from './campaign-audit-constants';
import {
  HealthTierLabel,
  HealthTierType,
  ParsedGuidance,
  RowSummary,
  SourceConfidenceClass,
  SourceConfidenceLabel,
  SourceConfidenceTitle,
  auditRowsToCsv,
  downloadCsv,
  healthTier,
  isInactive,
  isNoClientConfigError,
  parseManualGuidance,
  rowFlag,
  retryAsync,
  rolesForCategory,
  rowMatchesRole,
  summarizeRows
} from './campaign-audit-utils';
import {
  AutomationTier,
  AuditPayload,
  AuditRow,
  ClientSummary,
  DoctrineEntry,
  fetchAudit,
  fetchDoctrine,
  getClients,
  GuidanceRecordInput,
  Origin,
  revertToAuto,
  runAudit,
  saveAction,
  saveGuidance,
  SaveAnswerExtras,
  saveManualAnswer,
  saveOverride,
  ThemeDef
} from './auditApi';
import { ClientOnboarding } from './ClientOnboarding';
import { DoctrineStatus, Verdict } from './engine/types';

const DISCLAIMER_TITLE_ID = 'audit-disclaimer-title';
// Read the backend's catalog fingerprint without waiting on a bpm-types release — the same
// decoupling the lambda uses for its own local payload additions. Undefined against any backend that
// does not send it, which is what keeps the two repos independently revertable.
const catalogVersionOf = (payload: AuditPayload): string | undefined =>
  (payload as AuditPayload & { catalogVersion?: string }).catalogVersion;

export const CampaignAudit = () => {
  const { cid, name: clientName } = useCompanyInfo();
  const navigate = useNavigate();
  // The Guidance tab (doctrine authoring) is admin-only — gated on the same role === 0 ("Admin")
  // flag the User Admin page uses. Non-admins never see the nav item or reach the tab.
  const isAdmin = useAppSelector(UserRedux.isAdminSelector);
  const [payload, setPayload] = useState<AuditPayload | null>(null);
  const [doctrine, setDoctrine] = useState<DoctrineEntry[] | null>(null);
  const [doctrineError, setDoctrineError] = useState('');
  const [loading, setLoading] = useState(true);
  // Page-level error state is ONLY for the initial audit LOAD — a load failure legitimately replaces
  // the body with a retry panel. Mutation failures (save / run / override / revert / action) must NOT
  // reuse it, or a recoverable hiccup wipes the user's in-progress work off the screen (the reported
  // "it kicked me out"). Those go through the shared error modal instead, over an intact page.
  const [error, setError] = useState('');
  const showActionError = useSetError();
  // The client id resolves through an async companies → route → config chain. If it never arrives
  // (client not set up for this tool, or the user lacks access — a routing-layer issue tracked
  // separately), don't spin forever: after a short grace period we show an actionable message instead
  // of an endless spinner (the reported "no client config" loop).
  const [cidGraceExpired, setCidGraceExpired] = useState(false);
  // Distinct from `error`: the client resolved but isn't onboarded to the tool (no config row). That's a
  // setup state, not a failure — route it to an "Add client" CTA instead of the red load-error panel.
  const [noConfig, setNoConfig] = useState(false);
  const [running, setRunning] = useState(false);
  // Land on WORK, not documentation. This used to open on a ~307-line how-to document behind a
  // blocking modal — the first thing a new user saw (Harry 2026-09-09).
  const [activeTab, setActiveTab] = useState<string>(ACTIONS_KEY);
  const [detailRowId, setDetailRowId] = useState<number | null>(null);
  const detailRow = useMemo(
    () => (detailRowId !== null ? (payload?.rows.find(r => r.id === detailRowId) ?? null) : null),
    [payload, detailRowId]
  );
  const [showPicker, setShowPicker] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Client name to seed Add client's search with, set when the user picks a "needs setup" row.
  const [onboardQuery, setOnboardQuery] = useState('');
  // CAB-5 deep-link: the row a triage/overview click wants to jump to. Drives a tab switch + a
  // scroll-into-view + a transient highlight on that row; cleared shortly after so the highlight fades.
  const [scrollTargetRowId, setScrollTargetRowId] = useState<number | null>(null);
  // Internal-use disclaimer: shown on EVERY arrival at the page (Harry 2026-09-09), because it
  // carries the internal-only caveat and the bug-intake link. Deliberately not persisted anywhere —
  // it was previously acknowledged once per sessionStorage session, which meant that after the first
  // acknowledgement it never appeared again for the life of the tab.
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  const load = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    setError('');
    setNoConfig(false);
    try {
      // Retry transient load failures (async run not yet delivered / cold start) before giving up, so a
      // first-visit hiccup doesn't make the user click "retry" a few times (the reported flakiness). A
      // permanent "no client config" error short-circuits the retries and routes to the onboarding CTA.
      setPayload(
        await retryAsync(() => fetchAudit(cid), {
          attempts: 3,
          delayMs: 800,
          shouldStop: isNoClientConfigError
        })
      );
    } catch (e) {
      if (isNoClientConfigError(e)) {
        setNoConfig(true);
      } else {
        setError(e.message);
        // The inline retry panel is the user-facing surface here; this call is what carries the
        // failure to error monitoring via reportError (see redux/modals.ts). Load failures were
        // previously invisible to error monitoring (DEV-23582 #5).
        showActionError({ message: `Couldn't load the audit: ${e.message}`, reportError: e });
      }
    } finally {
      setLoading(false);
    }
  }, [cid, showActionError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (cid) {
      setCidGraceExpired(false);
      return undefined;
    }
    const t = setTimeout(() => setCidGraceExpired(true), 8000);
    return () => clearTimeout(t);
  }, [cid]);

  useEffect(() => {
    if (activeTab !== GUIDANCE_KEY || !isAdmin || doctrine || doctrineError) return;
    fetchDoctrine()
      .then(setDoctrine)
      .catch(e => {
        setDoctrineError(e.message);
        showActionError({
          message: `Couldn't load guidance doctrine: ${e.message}`,
          reportError: e
        });
      });
  }, [activeTab, isAdmin, doctrine, doctrineError, showActionError]);

  const onRun = async () => {
    if (!cid) return;
    setRunning(true);
    setError('');
    try {
      // runAudit persists the new run and returns its payload (but with no run-over-run delta —
      // that's only computed by GET /audit against the immediately-prior run). Re-fetch via
      // fetchAudit so the "Since last run" strip populates immediately after a run instead of
      // vanishing until a manual page reload.
      const runResult = await runAudit(cid);
      // A read immediately after the write can 404 if the backend read-path isn't yet consistent;
      // fetchAudit maps 404 -> null. Don't let that blank out the run we just completed — fall back
      // to the run's own payload (the delta strip fills in on the next load).
      const withDelta = await fetchAudit(cid).catch(() => null);
      setPayload(withDelta ?? runResult);
    } catch (e) {
      showActionError({ message: `Couldn't run the audit: ${e.message}`, reportError: e });
    } finally {
      setRunning(false);
    }
  };

  // verdict omitted → note-without-override (F31): the note is saved but the auto verdict stays.
  const onSaveAnswer = useCallback(
    async (id: number, verdict: Verdict | undefined, answer: string, extras?: SaveAnswerExtras) => {
      if (!cid) return;
      try {
        setPayload(await saveManualAnswer(cid, id, verdict, answer, extras));
      } catch (e) {
        showActionError({ message: `Couldn't save your answer: ${e.message}`, reportError: e });
      }
    },
    [cid, showActionError]
  );

  // F16 — revert a row to its auto read (drops the manual answer/override).
  const onRevertToAuto = useCallback(
    async (id: number) => {
      if (!cid) return;
      try {
        setPayload(await revertToAuto(cid, id));
      } catch (e) {
        showActionError({ message: `Couldn't revert to auto: ${e.message}`, reportError: e });
      }
    },
    [cid, showActionError]
  );

  const onSaveOverride = useCallback(
    async (id: number, notApplicable: boolean, reason?: string) => {
      if (!cid) return;
      try {
        setPayload(await saveOverride(cid, id, notApplicable, reason));
      } catch (e) {
        showActionError({ message: `Couldn't update N/A status: ${e.message}`, reportError: e });
      }
    },
    [cid, showActionError]
  );

  // CAB-8 — persist an Actions "addressed" toggle, keyed by the current run. Patches the run's
  // addressedRowIds in place from the endpoint's authoritative response (no full re-fetch).
  const runId = payload?.runId;
  const onToggleAction = useCallback(
    async (rowId: number, addressed: boolean) => {
      if (!cid || runId == null) return;
      try {
        const addressedRowIds = await saveAction(cid, runId, rowId, addressed);
        setPayload(prev => (prev ? { ...prev, addressedRowIds } : prev));
      } catch (e) {
        showActionError({
          message: `Couldn't update the addressed status: ${e.message}`,
          reportError: e
        });
      }
    },
    [cid, runId, showActionError]
  );

  // CAB-5 — jump from a triage/overview item to the exact row in its category tab.
  const onGoToRow = useCallback((row: AuditRow) => {
    setActiveTab(row.category);
    setScrollTargetRowId(row.id);
  }, []);

  // Clear the deep-link target after the highlight has had time to show, so re-clicking the same row
  // re-triggers the scroll/highlight and the class doesn't linger.
  useEffect(() => {
    if (scrollTargetRowId === null) return undefined;
    const t = setTimeout(() => setScrollTargetRowId(null), 2000);
    return () => clearTimeout(t);
  }, [scrollTargetRowId]);

  const onSaveGuidance = useCallback(
    async (target: { questionId: number }, guidance: GuidanceRecordInput) => {
      setDoctrine(await saveGuidance(target, guidance));
    },
    []
  );

  const navs: PageNav[] = useMemo(
    () => [
      { label: 'Needs attention', key: ACTIONS_KEY },
      { label: 'Overview', key: OVERVIEW_KEY },
      { label: 'Audit questions', key: QUESTIONS_KEY },
      // How to use moves LAST: it is reference material, not a landing page.
      { label: 'How to use', key: HOWTO_KEY }
    ],
    []
  );

  return (
    <Page
      title='TVAD Campaign Audit'
      pageType='TVAD Campaign Audit'
      navs={navs}
      selectedNav={activeTab}
      onNav={setActiveTab}
      minWidth='960px'
    >
      <div className='campaign-audit'>
        {showDisclaimer && <AuditDisclaimer onClose={() => setShowDisclaimer(false)} />}
        <V1Banner />
        <div className='client-switcher-bar'>
          <span className='client-name'>{clientName || cid}</span>
          <Button
            size={Size.LG}
            appearance={Appearance.OUTLINED}
            onClick={() => setShowOnboarding(true)}
          >
            Add client
          </Button>
          <Button
            size={Size.LG}
            appearance={Appearance.OUTLINED}
            onClick={() => setShowPicker(true)}
          >
            Change client
          </Button>
          {/* Run audit lives here rather than only on Overview (Harry 2026-09-14) — the switcher bar
              renders above the tabs, so the three actions a user needs are in one place from every
              tab, including the not-set-up empty state. Disabled until there is a configured client
              to run against. */}
          <Button
            size={Size.LG}
            onClick={onRun}
            loading={running}
            disabled={running || !cid || noConfig}
            title={
              noConfig || !cid
                ? 'Set this client up with Add client before running an audit'
                : 'Pull live data and re-grade every automated question'
            }
          >
            Run audit
          </Button>
          <InfoTooltip placement={Placement.BOTTOM}>
            <strong>Add client</strong> — links a new client&apos;s Bliss Point ID to their Nexxen
            and Flashtalking advertiser IDs. This is a one-time setup required before auto-row
            evaluations can run for that client. Search by the client&apos;s name to find matches.
            <br />
            <br />
            <strong>Change client</strong> — switches this audit view to another client. The list
            separates clients already set up for auditing from those that still need setup.
          </InfoTooltip>
        </div>
        {showOnboarding && (
          <ClientOnboarding
            initialQuery={onboardQuery}
            onConfirm={newCid => {
              setShowOnboarding(false);
              navigate(`/${newCid}/campaign-audit`);
            }}
            onClose={() => setShowOnboarding(false)}
          />
        )}
        {showPicker && (
          <ClientPicker
            currentCid={cid}
            onSelect={newCid => {
              setShowPicker(false);
              navigate(`/${newCid}/campaign-audit`);
            }}
            onSetUp={name => {
              setShowPicker(false);
              setOnboardQuery(name);
              setShowOnboarding(true);
            }}
            onClose={() => setShowPicker(false)}
          />
        )}
        {(() => {
          // Client id never resolved (routing / access) — the 8s-grace fallback.
          if (!cid && cidGraceExpired && activeTab !== HOWTO_KEY) {
            return (
              <div className='service-error'>
                <div className='error-title'>Couldn&apos;t load this client</div>
                <div className='error-detail'>
                  This client may not be set up for TVAD Campaign Audit yet, or you may not have
                  access to it.
                </div>
                <div className='setup-actions'>
                  <Button size={Size.LG} onClick={() => setShowOnboarding(true)}>
                    Add client
                  </Button>
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    onClick={() => setShowPicker(true)}
                  >
                    Change client
                  </Button>
                </div>
              </div>
            );
          }
          // Client resolved but isn't onboarded to the tool — a setup state, not a load failure.
          if (noConfig && activeTab !== HOWTO_KEY) {
            return (
              <div className='service-error'>
                <div className='error-title'>
                  This client isn&apos;t set up for TVAD Campaign Audit yet
                </div>
                <div className='error-detail'>
                  Link {clientName || cid} to its Nexxen and Flashtalking advertiser IDs — a
                  one-time setup — then run the audit.
                </div>
                {/* The buttons live here, not just in the bar above: this is the screen a
                    first-time user lands on, and telling them to look elsewhere is what made
                    getting started unclear (Diana, 2026-09-14). */}
                <div className='setup-actions'>
                  <Button
                    size={Size.LG}
                    onClick={() => {
                      setOnboardQuery(clientName || '');
                      setShowOnboarding(true);
                    }}
                  >
                    Set up {clientName || cid}
                  </Button>
                  <Button
                    size={Size.LG}
                    appearance={Appearance.OUTLINED}
                    onClick={() => setShowPicker(true)}
                  >
                    Pick another client
                  </Button>
                </div>
              </div>
            );
          }
          return (
            <AuditBody
              loading={loading}
              error={error}
              payload={payload}
              doctrine={doctrine}
              doctrineError={doctrineError}
              activeTab={activeTab}
              isAdmin={isAdmin}
              running={running}
              onRun={onRun}
              onRetry={load}
              onRetryDoctrine={() => setDoctrineError('')}
              onSaveAnswer={onSaveAnswer}
              onRevertToAuto={onRevertToAuto}
              onSaveOverride={onSaveOverride}
              onSaveGuidance={onSaveGuidance}
              onToggleAction={onToggleAction}
              onGoToRow={onGoToRow}
              scrollTargetRowId={scrollTargetRowId}
              onShowDetail={row => setDetailRowId(row.id)}
            />
          );
        })()}
        {detailRow && <RowDetailModal row={detailRow} onClose={() => setDetailRowId(null)} />}
      </div>
    </Page>
  );
};

// V1 framing banner (rollout, 2026-09): sets expectations for the broadened audience — the tool is
// new and evolving, some sections are still manual, more connectors are coming. Dismissible, and the
// dismissal sticks across sessions so it isn't a recurring nag.
// Deliberately NOT dismissable (Harry 2026-09-14). It now carries the one instruction first-time
// users were missing — Diana and others couldn't work out how to start an audit for a new client —
// so hiding it defeats the point. Removing the dismissal also deletes the page's last localStorage
// key: any stale `campaignAuditV1BannerDismissed` value sitting in a browser is simply ignored now,
// which matters because anyone who had already dismissed this banner would otherwise never see the
// new setup line.
const V1Banner = () => (
  <div className='v1-banner' role='status'>
    <div className='v1-banner-setup'>
      First time running an audit for this client? Click <strong>Add client</strong> to link their
      Nexxen and Flashtalking IDs — a one-time setup — then <strong>Run audit</strong>.
    </div>
    <span className='v1-banner-text'>
      <strong>TVAD Campaign Audit is a V1.</strong> It&apos;s new and will keep improving — some
      Relationship and Strategic questions are still answered by hand, and more DSP connectors are
      on the way. Treat every finding as a prompt to investigate, and send feedback so we can
      sharpen it.
    </span>
  </div>
);

// Internal-use disclaimer. Shown on arrival at the audit page; dismissable only via the
// acknowledge button (backdrop clicks do not close it), so the reader has to see it.
const AuditDisclaimer = ({ onClose }: { onClose: () => void }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Move focus into the dialog on mount so keyboard users start on the acknowledge button.
  useLayoutEffect(() => {
    dialogRef.current?.querySelector('button')?.focus();
  }, []);

  return (
    <div className='audit-disclaimer-backdrop'>
      <div
        ref={dialogRef}
        className='audit-disclaimer'
        role='dialog'
        aria-modal='true'
        aria-labelledby={DISCLAIMER_TITLE_ID}
      >
        <div className='disclaimer-title' id={DISCLAIMER_TITLE_ID}>
          Before you use this tool
        </div>
        <p className='disclaimer-body'>
          This campaign audit tool uses the Bliss Point MCP, certain API connections, and AI tools
          to provide a heuristic audit for a client. The results of this audit are to help guide
          your investigations;{' '}
          <strong>
            the results should not be taken to be absolute truth and may contain inaccuracies due to
            data availability
          </strong>
          . Tinuitians should review automated data pulls to confirm accuracy, and results should
          not be shared with clients &mdash; this is an internal tool for internal guidance and
          alerting. Please note that the results and entries of this audit are visible to all
          Tinuitians with access to this client&apos;s page.{' '}
          <strong>
            Bugs and data inaccuracies should be reported using{' '}
            <a href={AUDIT_INTAKE_FORM_URL} target='_blank' rel='noopener noreferrer'>
              this intake form
            </a>
            .
          </strong>
        </p>
        <div className='disclaimer-actions'>
          {/* The acknowledge button is the only focusable element; trapping Tab on it keeps a
              keyboard user from moving into the findings still mounted behind this required
              disclaimer. */}
          <Button
            size={Size.LG}
            onClick={onClose}
            onKeyDown={e => {
              if (e.key === 'Tab') {
                e.preventDefault();
              }
            }}
          >
            I understand
          </Button>
        </div>
      </div>
    </div>
  );
};

interface ClientPickerProps {
  currentCid: string;
  onSelect: (cid: string) => void;
  // Picking a client that has no config yet sends the user into Add client instead of navigating to
  // an audit that cannot run. Passes the client NAME, which is what Add client searches on.
  onSetUp: (name: string) => void;
  onClose: () => void;
}

const ClientPicker = ({ currentCid, onSelect, onSetUp, onClose }: ClientPickerProps) => {
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [clientsError, setClientsError] = useState('');
  const showError = useSetError();

  useEffect(() => {
    getClients()
      .then(setClients)
      .catch(e => {
        setClientsError(e.message);
        showError({ message: `Couldn't load the client list: ${e.message}`, reportError: e });
      });
  }, [showError]);

  // Two lists (Harry 2026-09-14): everything stays visible, and which is which is obvious. Diana
  // couldn't tell from the old flat list of every BPM company which ones were actually ready to
  // audit. Filtered separately so a long unconfigured tail can't crowd the configured list out of
  // a shared result limit.
  const configuredClients = useMemo(() => (clients ?? []).filter(c => c.configured), [clients]);
  const unconfiguredClients = useMemo(() => (clients ?? []).filter(c => !c.configured), [clients]);
  const filteredConfigured = useFilteredList(configuredClients, search, {
    keys: ['name', 'cid'],
    limit: 50
  });
  const filteredUnconfigured = useFilteredList(unconfiguredClients, search, {
    keys: ['name', 'cid'],
    limit: 50
  });
  const noMatches =
    Boolean(clients) && filteredConfigured.length === 0 && filteredUnconfigured.length === 0;

  return (
    <InteractiveDiv className='client-picker-overlay' onClick={onClose}>
      <InteractiveDiv className='client-picker' onClick={e => e.stopPropagation()}>
        <div className='client-picker-header'>
          <Form.Control
            autoFocus
            placeholder='Search clients…'
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className='client-picker-list'>
          {!clients && !clientsError && <div className='client-picker-loading'>Loading…</div>}
          {clientsError && <div className='client-picker-loading'>{clientsError}</div>}
          {noMatches && (
            <div className='client-picker-loading'>
              {clients?.length === 0 ? 'No clients available.' : `No client matches “${search}”.`}
            </div>
          )}
          {filteredConfigured.length > 0 && (
            <>
              <div className='client-picker-section'>
                Set up for auditing
                <span className='client-picker-count'>{configuredClients.length}</span>
              </div>
              {filteredConfigured.map(c => (
                <InteractiveDiv
                  key={c.cid}
                  className={cn('client-picker-row', { 'is-current': c.cid === currentCid })}
                  onClick={() => onSelect(c.cid)}
                >
                  <span className='client-picker-name'>{c.name}</span>
                  <span className='client-picker-cid'>{c.cid}</span>
                </InteractiveDiv>
              ))}
            </>
          )}
          {filteredUnconfigured.length > 0 && (
            <>
              <div className='client-picker-section'>
                Needs setup first
                <span className='client-picker-count'>{unconfiguredClients.length}</span>
              </div>
              <p className='client-picker-section-note'>
                These have no Nexxen or Flashtalking link yet, so an audit can&apos;t run. Pick one
                to start its one-time setup.
              </p>
              {filteredUnconfigured.map(c => (
                <InteractiveDiv
                  key={c.cid}
                  className='client-picker-row is-unconfigured'
                  onClick={() => onSetUp(c.name)}
                >
                  <span className='client-picker-name'>{c.name}</span>
                  <span className='client-picker-cid'>{c.cid}</span>
                  <span className='client-picker-setup'>Set up →</span>
                </InteractiveDiv>
              ))}
            </>
          )}
        </div>
      </InteractiveDiv>
    </InteractiveDiv>
  );
};

interface AuditBodyProps {
  loading: boolean;
  error: string;
  payload: AuditPayload | null;
  doctrine: DoctrineEntry[] | null;
  doctrineError: string;
  activeTab: string;
  isAdmin: boolean;
  running: boolean;
  onRun: () => void;
  onRetry: () => void;
  onRetryDoctrine: () => void;
  onSaveAnswer: (
    id: number,
    verdict: Verdict | undefined,
    answer: string,
    extras?: SaveAnswerExtras
  ) => void;
  onRevertToAuto: (id: number) => void;
  onSaveOverride: (id: number, notApplicable: boolean, reason?: string) => void;
  onSaveGuidance: (target: { questionId: number }, guidance: GuidanceRecordInput) => Promise<void>;
  onToggleAction: (rowId: number, addressed: boolean) => void;
  onGoToRow: (row: AuditRow) => void;
  scrollTargetRowId: number | null;
  onShowDetail: (row: AuditRow) => void;
}

const AuditBody = ({
  loading,
  error,
  payload,
  doctrine,
  doctrineError,
  activeTab,
  isAdmin,
  running,
  onRun,
  onRetry,
  onRetryDoctrine,
  onSaveAnswer,
  onRevertToAuto,
  onSaveOverride,
  onSaveGuidance,
  onToggleAction,
  onGoToRow,
  scrollTargetRowId,
  onShowDetail
}: AuditBodyProps) => {
  // How to use is a static guide and the default landing tab, so it renders regardless of whether
  // an audit has been run yet — before the loading/error/empty guards below.
  if (activeTab === HOWTO_KEY) return <HowToUseTab />;

  if (loading) return <Spinner />;
  if (error) return <ServiceError error={error} onRetry={onRetry} />;
  if (!payload) return <NoAuditYet running={running} onRun={onRun} />;

  if (activeTab === OVERVIEW_KEY) {
    return <OverviewTab payload={payload} running={running} onGoToRow={onGoToRow} />;
  }
  if (activeTab === ACTIONS_KEY)
    return <ActionsTab payload={payload} onToggleAction={onToggleAction} onGoToRow={onGoToRow} />;
  if (activeTab === GUIDANCE_KEY) {
    // The Guidance nav entry was removed for everyone (Harry 2026-09-09) — doctrine authoring is not
    // something the tool needs to expose in-app, and the overlay it writes still feeds every row.
    // This branch is now unreachable from the UI; it (and GuidanceTab) stay so restoring the tab is a
    // one-line change. Still admin-gated, in case the entry ever comes back.
    if (!isAdmin) return <HowToUseTab />;
    return (
      <GuidanceTab
        doctrine={doctrine}
        error={doctrineError}
        onRetry={onRetryDoctrine}
        onSaveGuidance={onSaveGuidance}
      />
    );
  }

  // One consolidated questions tab replaces the four category tabs.
  return (
    <QuestionsTab
      payload={payload}
      onSaveAnswer={onSaveAnswer}
      onRevertToAuto={onRevertToAuto}
      onSaveOverride={onSaveOverride}
      scrollTargetRowId={scrollTargetRowId}
      onShowDetail={onShowDetail}
    />
  );
};

const NoAuditYet = ({ running, onRun }: { running: boolean; onRun: () => void }) => (
  <div className='service-error'>
    <div className='error-title'>No audit run yet for this client</div>
    <div className='error-detail'>
      Run the audit to pull this client&apos;s live BPM/DSP/ad-server signals and grade every rule
      against The Tinuiti Way. It takes a minute or two; you can answer the manual questions while
      it works.
    </div>
    <Button size={Size.LG} onClick={onRun} loading={running} disabled={running}>
      Run audit
    </Button>
  </div>
);

const ServiceError = ({ error, onRetry }: { error: string; onRetry: () => void }) => (
  <div className='service-error'>
    <div className='error-title'>Could not load audit data</div>
    {error && <div className='error-detail'>{error}</div>}
    <Button size={Size.LG} onClick={onRetry}>
      Retry
    </Button>
  </div>
);

// ── How to use ──────────────────────────────────────────────────────────────

// Static onboarding guide — the reference tab (Needs attention is the landing tab). Walks a new
// user end-to-end through launching and completing an audit for any client. Renders without a
// payload, so it also stands in when a client has no config yet.
const HowToUseTab = () => (
  <div className='how-to-use-tab'>
    <div className='howto-intro'>
      <h2 className='howto-title'>How to run a TVAD Campaign Audit</h2>
      <p>
        The TVAD Campaign Audit turns a client&apos;s live setup into an{' '}
        <strong>investigation queue</strong> — a list of things worth a closer look. It is not a
        0–100 score or a grade: each applicable question comes back <strong>Clear</strong>,{' '}
        <strong>Recommend investigation</strong>, or <strong>Investigation required</strong>, and
        your job is to work through the flagged ones.
      </p>
      <p className='howto-callout'>
        Internal use only. Findings are prep for your own review — don&apos;t share the raw audit or
        the CSV export with clients.
      </p>
    </div>

    <ol className='howto-steps'>
      <li>
        <div className='howto-step-title'>1. Pick the client — or set one up</div>
        <p>
          If the client already exists, use <strong>Change client</strong> (top of the page) to
          switch the audit to them, and skip ahead to step 2. If the client is brand new, use{' '}
          <strong>Add client</strong> to set them up first — this is a one-time step, and it&apos;s
          the least intuitive part of the tool, so here it is in full.
        </p>

        <details className='howto-substep-block'>
          <summary className='howto-substep-title'>
            Setting up a new client for the first time
          </summary>
          <p>
            The audit pulls data from <strong>three separate systems</strong>, and each one has its
            own ID for what is really the same advertiser. Setting up a client is where you link
            those three IDs together into one client record, so the audit knows they&apos;re the
            same account:
          </p>
          <ul className='howto-systems'>
            <li>
              <strong>BPM (Bliss Point)</strong> — the core client record in our own platform.{' '}
              <strong>Required</strong> — everything hangs off this.
            </li>
            <li>
              <strong>Nexxen</strong> — the DSP. Linking it lets the audit auto-evaluate the{' '}
              <em>DSP rows</em>. Optional, but link it if the client runs on Nexxen.
            </li>
            <li>
              <strong>Flashtalking</strong> — the ad-server. Linking it lets the audit auto-evaluate
              the <em>Flashtalking rows</em>. Optional, but link it if the client uses Flashtalking.
            </li>
          </ul>
          <p>
            The three systems don&apos;t share IDs, so the tool searches all three by the name you
            type and shows its best guesses — but <strong>you</strong> confirm each link. Then:
          </p>
          <ol className='howto-substeps'>
            <li>
              Click <strong>Add client</strong> at the top of the page.
            </li>
            <li>
              Type the client&apos;s name (e.g. <em>Mistplay</em>) and click <strong>Search</strong>{' '}
              (or press Enter). The tool searches BPM, Nexxen, and Flashtalking at once.
            </li>
            <li>
              You&apos;ll get three sections — <strong>BPM client</strong> (Required),{' '}
              <strong>Nexxen advertiser</strong> (DSP rows), and{' '}
              <strong>Flashtalking advertiser</strong> (FT rows). Each shows candidate matches as
              pills, and every pill lists the advertiser <strong>name</strong>, its{' '}
              <strong>#ID</strong> in that system, and a <strong>match-confidence %</strong>.
            </li>
            <li>
              Pick the right match in each section:
              <ul>
                <li>
                  A match at <strong>80% confidence or higher is pre-selected</strong> for you — but
                  still glance at the name and #ID to make sure it&apos;s right.
                </li>
                <li>
                  Below 80%, <strong>nothing is pre-selected</strong> — you have to choose on
                  purpose. A wrong pick silently links the wrong advertiser, and that
                  advertiser&apos;s data then feeds the audit, so don&apos;t guess.
                </li>
                <li>
                  If the client genuinely isn&apos;t in Nexxen or Flashtalking, click{' '}
                  <strong>Skip</strong> in that section. You can&apos;t skip BPM — it&apos;s
                  required.
                </li>
              </ul>
            </li>
            <li>
              Read the summary line at the bottom. It spells out exactly what will be linked — e.g.{' '}
              <em>&ldquo;Will link Acme · Nexxen #12345 · Flashtalking #67890&rdquo;</em> — and
              calls out anything you skipped (
              <em>&ldquo;no Nexxen (DSP rows stay manual)&rdquo;</em>).
            </li>
            <li>
              Click <strong>Set up client</strong>. The link is saved and you land straight on that
              client&apos;s audit, ready for step 2.
            </li>
          </ol>
          <div className='howto-note'>
            <strong>Worth knowing:</strong>
            <ul>
              <li>
                This is a <strong>one-time</strong> setup per client. After it, just use{' '}
                <strong>Change client</strong> to come back — you won&apos;t repeat this.
              </li>
              <li>
                Skipping Nexxen or Flashtalking isn&apos;t fatal, but those rows then{' '}
                <strong>stay manual</strong> — you answer them by hand instead of the audit deriving
                them. Link a system whenever the client is actually in it.
              </li>
              <li>
                <strong>Double-check the #IDs</strong>, especially on low-confidence (&lt;80%)
                matches — a wrong link quietly pulls a different advertiser&apos;s numbers into this
                client&apos;s audit.
              </li>
              <li>
                If a section says <em>&ldquo;No BPM match — try a different spelling&rdquo;</em> (or
                &ldquo;Not found in Nexxen/Flashtalking&rdquo;), tweak the search term and try again
                — names often differ slightly between systems.
              </li>
            </ul>
          </div>
        </details>
      </li>
      <li>
        <div className='howto-step-title'>2. Run the audit</div>
        <p>
          Click <strong>Run audit</strong> at the top of the page. It sits next to Add client and
          Change client, so it&apos;s there on every tab. It fetches live BPM, DSP, and Flashtalking
          data and can take a minute or two. When it finishes, the auto rows are scored and the
          results appear across the tabs. You can re-run any time — each run is saved, so you can
          compare against the previous one.
        </p>
      </li>
      <li>
        <div className='howto-step-title'>3. Read the Overview</div>
        <p>
          The <strong>Overview</strong> is your one-glance read. The health tier (
          <strong>At-risk / Watch / Clear</strong>) sits up top, followed by the flag counts (how
          many questions need a look) and a per-category breakdown. Watch two things:
        </p>
        <ul>
          <li>
            The <strong>mission-critical banner</strong> — the highest-priority gaps; click through
            to answer them first.
          </li>
          <li>
            <strong>Coverage</strong> — the share of applicable questions answered so far. A short
            queue on thin coverage means there&apos;s still work to do, not that the client is
            healthy.
          </li>
        </ul>
        <p>
          After a second run, a <strong>&ldquo;Since last run&rdquo;</strong> strip shows what
          changed.
        </p>
      </li>
      <li>
        <div className='howto-step-title'>4. Work the queue in Needs attention</div>
        <p>
          <strong>Needs attention</strong> is the landing tab and where most of the work happens. It
          splits into two columns, because they are two different jobs:
        </p>
        <ul>
          <li>
            <strong>Flagged for investigation</strong> — everything the audit scored as worth a look
            this run, most severe first. Tick items off as you address them to watch the &ldquo;N/M
            addressed&rdquo; count climb. If a later run still flags the same item, it comes back
            unchecked so you re-confirm the earlier conclusion still holds.
          </li>
          <li>
            <strong>Needs your input</strong> — mission-critical questions with no answer yet.
            Nothing is wrong with them; nobody has answered them. Click one to jump straight to that
            row.
          </li>
        </ul>
        <p>
          Clicking any item takes you to the full row in <strong>Audit questions</strong>, where you
          answer it.
        </p>
      </li>
      <li>
        <div className='howto-step-title'>5. Answer and investigate in Audit questions</div>
        <p>
          <strong>Audit questions</strong> holds all the questions. It is also two columns:{' '}
          <strong>Mission-critical</strong> on the left, which stays in view as you scroll, and{' '}
          <strong>Everything else</strong> on the right, grouped into collapsible sections. Use{' '}
          <strong>Group by</strong> to switch those sections between <strong>Category</strong>{' '}
          (Relationship / Strategic / Tactical / Technical) and <strong>Theme</strong> (Measurement
          Readiness, Account Hygiene, and so on) — same questions, two ways of cutting them.{' '}
          <strong>View as</strong> narrows both columns to your seat&apos;s rows. For each row:
        </p>
        <ul>
          <li>Open the row to see the evidence and expected answer behind the flag.</li>
          <li>
            If you investigated and it&apos;s fine, record a <strong>manual answer</strong> — set
            the verdict and add a short note of what you found. Your note persists across future
            runs.
          </li>
          <li>
            If the question doesn&apos;t apply to this client, mark it <strong>N/A</strong> with a
            reason so it drops out of the queue. Use <strong>Show N/A</strong> to review what has
            already been excluded.
          </li>
        </ul>
      </li>
      <li>
        <div className='howto-step-title'>6. Re-run and compare</div>
        <p>
          Before a client meeting (or after making changes), run the audit again and read the
          &ldquo;Since last run&rdquo; strip to see what moved. Use <strong>Export CSV</strong> on
          the Overview for your own prep — internal only.
        </p>
      </li>
    </ol>

    <div className='howto-aside'>
      <div className='howto-reference'>
        <h3 className='howto-subtitle'>Quick reference</h3>
        <ul>
          <li>
            <strong>Verdicts</strong> — <em>Clear</em> (looks fine),{' '}
            <em>Recommend investigation</em> (worth a look), <em>Investigation required</em> (needs
            a look now). A flag that turns out fine isn&apos;t &ldquo;wrong&rdquo; — it&apos;s an
            investigation that came back clean.
          </li>
          <li>
            <strong>Auto vs. manual rows</strong> — auto rows are derived live from BPM / DSP /
            Flashtalking data; manual rows are answered by you. Auto rows carry a source-confidence
            badge (AI-derived, heuristic, or a direct measured read).
          </li>
          <li>
            <strong>Doctrine badge</strong> — <em>Pinned</em> means a verbatim Tinuiti Way citation
            backs the call; <em>Unpinned</em> means it&apos;s doctrine-aligned but not yet pinned.
          </li>
        </ul>
      </div>

      <div className='howto-cadence'>
        <h3 className='howto-subtitle'>Who runs what, and how often</h3>
        <p>
          The audit spans four kinds of question, each with a different owner and cadence. Use{' '}
          <strong>View as</strong> — on <strong>Overview</strong>, <strong>Needs attention</strong>{' '}
          and <strong>Audit questions</strong> — to narrow every list to just your seat&apos;s rows.
        </p>
        <ul className='howto-cadence-list'>
          <li>
            <strong>Technical</strong> — execution / compliance (tagging, ad server, hygiene,
            delivery). Owner: <strong>Media Investment</strong>. Cadence: frequent — most are
            automatable; check monthly or on any setup change.
          </li>
          <li>
            <strong>Tactical</strong> — the levers (targeting, 1P data, bidding, structure). Owner:{' '}
            <strong>Media Investment + TVAD Lead</strong>. Cadence: quarterly, or whenever the plan
            changes.
          </li>
          <li>
            <strong>Strategic</strong> — measurement validity and growth (MMM / MTA /
            incrementality, full-funnel readiness). Owner:{' '}
            <strong>TVAD Lead + Client Partner</strong>. Cadence: quarterly to semi-annual — this is
            critical-thinking, not a checklist.
          </li>
          <li>
            <strong>Relationship</strong> — engagement, leadership buy-in, churn / growth signals.
            Owner: <strong>Client Partner</strong>. Cadence: ongoing — revisit each quarter.
          </li>
        </ul>
      </div>

      <div className='howto-faq'>
        <h3 className='howto-subtitle'>FAQ</h3>
        <dl className='howto-faq-list'>
          <dt>Is this a score or a grade?</dt>
          <dd>
            No. It&apos;s an investigation queue — counts of what&apos;s worth a look, plus a
            coverage read. There&apos;s no 0–100 number, and the counts aren&apos;t comparable
            across clients.
          </dd>

          <dt>Why are so many Relationship / Strategic questions blank or manual?</dt>
          <dd>
            Those depend on context we can&apos;t yet pull automatically — call notes, leadership
            signals, brand ambitions. They&apos;re answered by hand today; auto-population via
            Client Oracle is on the roadmap.
          </dd>

          <dt>My client shows almost no data, or &ldquo;isn&apos;t set up&rdquo;.</dt>
          <dd>
            The auto rows currently derive from <strong>Nexxen</strong> (DSP) and{' '}
            <strong>Flashtalking</strong> (ad server). A client not on those — e.g. on Amazon DSP or
            The Trade Desk — will have most DSP / FT rows fall back to manual; those connectors are
            planned. If the client just isn&apos;t linked yet, use <strong>Add client</strong>.
          </dd>

          <dt>The tool flagged something that&apos;s actually fine, or already explained.</dt>
          <dd>
            Expected — a flag is a prompt, not a verdict. Record a <strong>manual answer</strong>{' '}
            with a short note (e.g. &ldquo;YouTube is run in-house; we pitched it, the client
            declined&rdquo;), or <strong>accept it as a known gap</strong> with a rationale and an
            optional revisit date. Both drop it out of the active queue while keeping your reasoning
            on the record.
          </dd>

          <dt>An auto answer looks wrong.</dt>
          <dd>
            Auto rows read real data through a proxy and can miss edge cases (a 1P segment applied a
            way the deriver didn&apos;t expect, say). Check the evidence on the row, and if
            it&apos;s off, override it with a manual answer — then flag persistent misses to the
            tool owner so the logic can be fixed.
          </dd>

          <dt>Could a big swing or a soft trend just be seasonal?</dt>
          <dd>
            Often, yes. The volume / efficiency rows and the data-hygiene check discount seasonality
            where they can, but a spiky or soft reading can still be a normal seasonal pattern —
            confirm before treating it as a real problem.
          </dd>

          <dt>How often should I run it?</dt>
          <dd>
            See &ldquo;Who runs what, and how often&rdquo; above — cadence follows the category.
          </dd>

          <dt>Can I share this with the client?</dt>
          <dd>
            No. It&apos;s internal prep, and heuristic / AI-derived in places. Don&apos;t share the
            audit or the CSV export with clients.
          </dd>
        </dl>
      </div>
    </div>
  </div>
);

// ── Collapsible section (digestibility) ──────────────────────────────────────
// A behemoth page overwhelmed new users and made data hard to validate (Harry, 2026-09; echoes Liat /
// Lindsey). This wraps a block in a click-to-toggle header + count summary. `defaultOpen` seeds the
// smart default (open only what needs a look); `forceOpen` pops it open when a deep-linked row lands
// inside a collapsed section (and keeps it open afterward, so the row doesn't vanish when the highlight
// fades).
interface CollapsibleSectionProps {
  title: ReactNode;
  summary?: ReactNode;
  defaultOpen?: boolean;
  forceOpen?: boolean;
  className?: string;
  children: ReactNode;
}

const CollapsibleSection = ({
  title,
  summary,
  defaultOpen = false,
  forceOpen = false,
  className,
  children
}: CollapsibleSectionProps) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);

  return (
    <div className={cn('collapsible-section', className, { 'is-open': open })}>
      <InteractiveDiv className='collapsible-head' onClick={() => setOpen(o => !o)}>
        <span className='collapsible-caret'>{open ? '▾' : '▸'}</span>
        <span className='collapsible-title'>{title}</span>
        {summary !== undefined && <span className='collapsible-summary'>{summary}</span>}
      </InteractiveDiv>
      {open && <div className='collapsible-body'>{children}</div>}
    </div>
  );
};

// A topic/section's flag count (Alarming + Concerning, applicable rows) — drives the "N flagged"
// summary and the smart-default open state.
const flaggedCount = (rows: AuditRow[]): number =>
  rows.filter(r => !isInactive(r) && (r.verdict === 'Alarming' || r.verdict === 'Concerning'))
    .length;

// ── Overview ──────────────────────────────────────────────────────────────

interface OverviewTabProps {
  payload: AuditPayload;
  running: boolean;
  onGoToRow: (row: AuditRow) => void;
}

// A row that needs attention: unanswered mission-critical, Alarming, or Concerning. Sorted so the
// most urgent (mission-critical gaps, then Alarming, then Concerning) float to the top.
const attentionRank = (row: AuditRow, mcUnanswered: Set<number>): number | null => {
  if (isInactive(row)) return null;
  if (mcUnanswered.has(row.id) && row.verdict === undefined) return 0;
  if (row.verdict === 'Alarming') return 1;
  if (row.verdict === 'Concerning') return 2;
  return null;
};

interface RoleFilterProps {
  value: AuditRoleType | null;
  onChange: (role: AuditRoleType | null) => void;
}

// Chip row to narrow the actionable lists to one seat's rows (Media Investment / TVAD Lead / Client
// Partner). "All" clears the filter. Role is derived from category, so this is really "show me the
// categories my seat owns" (Luke, alpha 2026-08).
const RoleFilter = ({ value, onChange }: RoleFilterProps) => (
  <div className='role-filter'>
    <span className='role-filter-label'>View as</span>
    <Button
      size={Size.SM}
      appearance={value === null ? Appearance.FILLED : Appearance.OUTLINED}
      onClick={() => onChange(null)}
    >
      All roles
    </Button>
    {AUDIT_ROLE_ORDER.map(role => (
      <Button
        key={role}
        size={Size.SM}
        appearance={value === role ? Appearance.FILLED : Appearance.OUTLINED}
        onClick={() => onChange(role)}
      >
        {AuditRoleLabel[role]}
      </Button>
    ))}
  </div>
);

const OverviewTab = ({ payload, running, onGoToRow }: OverviewTabProps) => {
  const mcUnanswered = useMemo(
    () => new Set(payload.missionCriticalUnansweredIds ?? []),
    [payload.missionCriticalUnansweredIds]
  );
  // Inline top-3 across the whole audit (CAB-5): the highest-priority findings, so the single most
  // important next actions are visible without opening a category. Deep-links straight to the row.
  const topPriorities = useMemo(() => {
    const ranked = payload.rows
      .map(row => ({ row, rank: attentionRank(row, mcUnanswered) }))
      .filter((x): x is { row: AuditRow; rank: number } => x.rank !== null);
    ranked.sort((a, b) => a.rank - b.rank || a.row.id - b.row.id);
    return ranked.slice(0, 3).map(x => x.row);
  }, [payload.rows, mcUnanswered]);
  const overall = useMemo(() => summarizeRows(payload.rows), [payload.rows]);
  // Role filter (Luke, alpha 2026-08): narrow the actionable lists to the rows a given seat owns, so a
  // two-person team can each see just their part. null = all roles. Filters the top-priority + needs-
  // attention surfaces (the "what do I act on" lists); the summary strips stay whole-audit.
  const [roleFilter, setRoleFilter] = useState<AuditRoleType | null>(null);
  const inRole = useCallback((r: AuditRow) => rowMatchesRole(r, roleFilter), [roleFilter]);
  const rowsByCategory = useMemo(() => {
    const map = new Map<string, AuditRow[]>();
    for (const category of CATEGORY_ORDER) {
      map.set(
        category,
        payload.rows.filter(r => r.category === category)
      );
    }
    return map;
  }, [payload.rows]);
  return (
    <div className='overview-tab'>
      <div className='overview-head'>
        <div className='overview-titles'>
          <div className='overview-account'>{payload.clientName}</div>
          <div className='overview-sub'>
            Last run {new Date(payload.runAt).toLocaleString()}
            {/* Catalog fingerprint for the build that produced this run. OPTIONAL — an older backend
                sends no such field, and this must render fine without it so the two repos stay
                independently revertable. Makes "did the change actually deploy?" answerable on
                screen instead of a log dig. */}
            {catalogVersionOf(payload) !== undefined && (
              <span
                className='overview-build'
                title='Catalog fingerprint for the build that produced this run: questions / automated / demoted / mission-critical'
              >
                build {catalogVersionOf(payload)}
              </span>
            )}
          </div>
          {running && (
            <div className='overview-running'>
              Fetching live data — this can take a minute or two…
            </div>
          )}
        </div>
        <div className='overview-actions'>
          <Button
            size={Size.LG}
            appearance={Appearance.OUTLINED}
            onClick={() =>
              downloadCsv(
                `tvad-campaign-audit-INTERNAL-${payload.cid}-${payload.runAt.slice(0, 10)}.csv`,
                auditRowsToCsv(payload.rows)
              )
            }
            title='Export findings to CSV for your own prep — internal use only, do not share with clients'
          >
            Export CSV
          </Button>
        </div>
      </div>

      <RoleFilter value={roleFilter} onChange={setRoleFilter} />

      <TopPriorities rows={topPriorities.filter(inRole)} onGoToRow={onGoToRow} />

      <FunnelBalance rows={payload.rows} onGoToRow={onGoToRow} />

      <div className='score-strip'>
        <FlagCountsHeadline summary={overall} />
        {CATEGORY_ORDER.map(category => (
          <CategoryCountCard
            key={category}
            label={AuditCategoryShortLabel[category]}
            rows={rowsByCategory.get(category)!}
            onGoToRow={onGoToRow}
          />
        ))}
      </div>

      <ThemeScorecards payload={payload} mcUnanswered={mcUnanswered} onGoToRow={onGoToRow} />
    </div>
  );
};

// ── Funnel balance (Overview rollup) ────────────────────────────────────────
// A curated read of the existing funnel-coverage rows in one place (Lindsey: "targeting all parts of
// the funnel"). NOT a new scored verdict — it surfaces the signals + how many need a look, each
// clickable through to its row.
interface FunnelBalanceProps {
  rows: AuditRow[];
  onGoToRow: (row: AuditRow) => void;
}

const FunnelBalance = ({ rows, onGoToRow }: FunnelBalanceProps) => {
  const byId = new Map(rows.map(r => [r.id, r]));
  const signals = FUNNEL_SIGNAL_IDS.map(s => ({ label: s.label, row: byId.get(s.id) })).filter(
    (s): s is { label: string; row: AuditRow } => s.row !== undefined
  );
  if (signals.length === 0) return null;

  const flagged = signals.filter(
    s => !isInactive(s.row) && (s.row.verdict === 'Alarming' || s.row.verdict === 'Concerning')
  ).length;

  return (
    <div className='funnel-balance'>
      <div className='section-title'>Funnel balance</div>
      <div className='funnel-balance-sub'>
        {flagged === 0
          ? 'No funnel-coverage signals are flagged — the account looks balanced across the funnel.'
          : `${flagged} of ${signals.length} funnel-coverage signals need a look.`}
      </div>
      <div className='funnel-balance-list'>
        {signals.map(({ label, row }) => (
          <InteractiveDiv
            key={row.id}
            className='funnel-balance-item'
            onClick={() => onGoToRow(row)}
          >
            <span className='funnel-balance-label'>{label}</span>
            {row.verdict ? (
              <VerdictPill verdict={row.verdict} />
            ) : (
              <span className='no-verdict'>—</span>
            )}
          </InteractiveDiv>
        ))}
      </div>
    </div>
  );
};

// ── Top priorities (inline top-3) ───────────────────────────────────────────

interface TopPrioritiesProps {
  rows: AuditRow[];
  onGoToRow: (row: AuditRow) => void;
}

// The single most important next actions across the whole audit (CAB-5), so the reader sees them
// without opening a category. Each deep-links to its row.
const TopPriorities = ({ rows, onGoToRow }: TopPrioritiesProps) => {
  if (rows.length === 0) return null;

  return (
    <div className='top-priorities'>
      <div className='section-title'>Top priorities</div>
      <div className='top-priorities-list'>
        {rows.map(row => (
          <InteractiveDiv
            key={row.id}
            className={cn('top-priority-item', row.verdict ? VerdictClass[row.verdict] : 'mc')}
            onClick={() => onGoToRow(row)}
          >
            <span
              className={cn('top-priority-dot', row.verdict ? VerdictClass[row.verdict] : 'mc')}
            />
            <span className='top-priority-severity'>
              {row.verdict ? VerdictLabel[row.verdict] : 'Unanswered'}
            </span>
            <span className='top-priority-question'>{row.question}</span>
            <span className='top-priority-cat'>{AuditCategoryShortLabel[row.category]} →</span>
          </InteractiveDiv>
        ))}
      </div>
    </div>
  );
};

// ── Theme scorecards ─────────────────────────────────────────────────────────

interface ThemeScorecardsProps {
  payload: AuditPayload;
  mcUnanswered: Set<number>;
  onGoToRow: (row: AuditRow) => void;
}

const ThemeScorecards = ({ payload, mcUnanswered, onGoToRow }: ThemeScorecardsProps) => {
  if (!payload.themes || payload.themes.length === 0) return null;

  const rowsById = new Map(payload.rows.map(r => [r.id, r]));

  return (
    <CollapsibleSection
      className='theme-section'
      title='By theme'
      summary={conditionallyPluralizeWithCount(payload.themes.length, 'theme')}
    >
      <div className='theme-grid'>
        {payload.themes.map(theme => (
          <ThemeCard
            key={theme.id}
            theme={theme}
            rowsById={rowsById}
            mcUnanswered={mcUnanswered}
            onGoToRow={onGoToRow}
          />
        ))}
      </div>
    </CollapsibleSection>
  );
};

interface ThemeCardProps {
  theme: ThemeDef;
  rowsById: Map<number, AuditRow>;
  mcUnanswered: Set<number>;
  onGoToRow: (row: AuditRow) => void;
}

const ThemeCard = ({ theme, rowsById, mcUnanswered, onGoToRow }: ThemeCardProps) => {
  const [showAll, setShowAll] = useState(false);
  const focus = useMemo(() => {
    const ranked = theme.questionIds
      .map(id => rowsById.get(id))
      .filter((r): r is AuditRow => Boolean(r))
      .map(row => ({ row, rank: attentionRank(row, mcUnanswered) }))
      .filter((x): x is { row: AuditRow; rank: number } => x.rank !== null);
    ranked.sort((a, b) => a.rank - b.rank || a.row.id - b.row.id);
    return ranked.map(x => x.row);
  }, [theme.questionIds, rowsById, mcUnanswered]);

  const themeRows = useMemo(
    () => theme.questionIds.map(id => rowsById.get(id)).filter((r): r is AuditRow => Boolean(r)),
    [rowsById, theme.questionIds]
  );
  const summary = useMemo(() => summarizeRows(themeRows), [themeRows]);
  const tier = healthTier(summary);

  return (
    <div className={cn('theme-card', tier)}>
      <div className='theme-card-head'>
        <span className='theme-label'>{theme.label}</span>
        <HealthTierBadge tier={tier} />
      </div>
      <div className='theme-coverage'>
        {summary.answered}/{summary.applicable} answered · coverage{' '}
        {Math.round(summary.coverage * 100)}%
      </div>
      <FlagChips summary={summary} rows={themeRows} onGoToRow={onGoToRow} />
      {focus.length > 0 && (
        <div className='theme-focus'>
          <div className='theme-focus-label'>Focus</div>
          {(showAll ? focus : focus.slice(0, 3)).map(row => (
            <InteractiveDiv
              key={row.id}
              className='theme-focus-item'
              onClick={() => onGoToRow(row)}
            >
              <span
                className={cn('focus-dot', row.verdict ? VerdictClass[row.verdict] : 'mc')}
                title={
                  row.verdict ? VerdictLabel[row.verdict] : 'Mission-critical — not yet answered'
                }
              />
              <span className='focus-question'>{row.question}</span>
            </InteractiveDiv>
          ))}
          {focus.length > 3 && (
            <InteractiveSpan className='theme-focus-more' onClick={() => setShowAll(s => !s)}>
              {showAll ? 'Show less' : `+${focus.length - 3} more`}
            </InteractiveSpan>
          )}
        </div>
      )}
    </div>
  );
};

const HealthTierBadge = ({ tier }: { tier: HealthTierType }) => (
  <span className={cn('tier-badge', tier)} title={`Health: ${HealthTierLabel[tier]}`}>
    {HealthTierLabel[tier]}
  </span>
);

// Top-line flag counts + coverage + health tier (CA-1) — replaces the old 0–100 headline. No score.
const FlagCountsHeadline = ({ summary }: { summary: RowSummary }) => {
  const tier = healthTier(summary);
  return (
    <div className={cn('flag-headline', tier)}>
      <div className='flag-headline-top'>
        <HealthTierBadge tier={tier} />
        <div className='flag-headline-counts'>
          <span className='flag-count required'>
            {VerdictLabel.Alarming}: <strong>{summary.required}</strong>
            {summary.mcRequired > 0 && (
              <span className='flag-count-mc'> ({summary.mcRequired} mission-critical)</span>
            )}
          </span>
          <span className='flag-count-sep'>·</span>
          <span className='flag-count recommend'>
            {VerdictLabel.Concerning}: <strong>{summary.recommend}</strong>
          </span>
        </div>
      </div>
      <div className='flag-headline-coverage'>
        coverage {Math.round(summary.coverage * 100)}% · {summary.answered}/{summary.applicable}{' '}
        answered
        {summary.mcUnanswered > 0 && ` · ${summary.mcUnanswered} mission-critical unanswered`}
      </div>
      {summary.lowConfidence > 0 && (
        <div
          className='flag-headline-confidence'
          title='These verdicts — including any "Clear" — come from AI or heuristic signals, not a direct measured read. Confirm before relying on them.'
        >
          {summary.lowConfidence} of {summary.answered} answered are AI/heuristic-derived — confirm
          before relying
        </div>
      )}
    </div>
  );
};

interface CategoryCountCardProps {
  label: string;
  rows: AuditRow[];
  onGoToRow: (row: AuditRow) => void;
}

const CategoryCountCard = ({ label, rows, onGoToRow }: CategoryCountCardProps) => {
  const summary = summarizeRows(rows);
  const tier = healthTier(summary);
  return (
    <div className={cn('score-card', tier)}>
      <div className='card-label'>{label}</div>
      <HealthTierBadge tier={tier} />
      <div className='card-coverage'>
        {summary.answered}/{summary.applicable}
      </div>
      <FlagChips summary={summary} rows={rows} onGoToRow={onGoToRow} />
    </div>
  );
};

interface FlagChipsProps {
  summary: RowSummary;
  rows: AuditRow[];
  onGoToRow: (row: AuditRow) => void;
}

// Compact severity-first chip row: required · recommend · clear · (unanswered). Reuses the verdict
// tone classes; each chip's full label is on hover. Clicking jumps to the first matching row —
// these used to be inert counts (Liat feedback 2026-08-04: "the colored numbers ... aren't
// clickable"); disabled when there's nothing of that kind to jump to.
const FlagChips = ({ summary, rows, onGoToRow }: FlagChipsProps) => {
  const goToFirst = (verdict: Verdict | undefined) => {
    const row = rows.find(r => !isInactive(r) && r.verdict === verdict);
    if (row) {
      onGoToRow(row);
    }
  };

  return (
    <div className='count-chips'>
      <InteractiveSpan
        className='chip alarming'
        title={`${VerdictLabel.Alarming}: ${summary.required}`}
        disabled={summary.required === 0}
        onClick={() => goToFirst('Alarming')}
      >
        {summary.required}
      </InteractiveSpan>
      <InteractiveSpan
        className='chip concerning'
        title={`${VerdictLabel.Concerning}: ${summary.recommend}`}
        disabled={summary.recommend === 0}
        onClick={() => goToFirst('Concerning')}
      >
        {summary.recommend}
      </InteractiveSpan>
      <InteractiveSpan
        className='chip good'
        title={`${VerdictLabel.Good}: ${summary.clear}`}
        disabled={summary.clear === 0}
        onClick={() => goToFirst('Good')}
      >
        {summary.clear}
      </InteractiveSpan>
      {summary.unanswered > 0 && (
        <InteractiveSpan
          className='chip unanswered'
          title={`Unanswered: ${summary.unanswered}`}
          onClick={() => goToFirst(undefined)}
        >
          {summary.unanswered}
        </InteractiveSpan>
      )}
    </div>
  );
};

// ── Actions tab ───────────────────────────────────────────────────────────────

interface ActionsTabProps {
  payload: AuditPayload;
  onToggleAction: (rowId: number, addressed: boolean) => void;
  onGoToRow: (row: AuditRow) => void;
}

const ActionsTab = ({ payload, onToggleAction, onGoToRow }: ActionsTabProps) => {
  // "Addressed" state is a per-run acknowledgment persisted server-side (CAB-8, tvad_audit_actions).
  // Seed from the payload's addressedRowIds and keep an optimistic local mirror so the checkbox is
  // instant; re-sync whenever the persisted set changes (e.g. after a re-run or the POST response).
  const [resolved, setResolved] = useState<Set<number>>(
    () => new Set(payload.addressedRowIds ?? [])
  );

  useEffect(() => {
    setResolved(new Set(payload.addressedRowIds ?? []));
  }, [payload.addressedRowIds]);

  const toggle = (id: number) => {
    const addressed = !resolved.has(id);
    setResolved(prev => {
      const next = new Set(prev);
      if (addressed) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
    onToggleAction(id, addressed);
  };

  const [role, setRole] = useState<AuditRoleType | null>(null);
  const inRole = useCallback(
    (r: AuditRow) => role === null || (ROLES_BY_CATEGORY[r.category]?.includes(role) ?? false),
    [role]
  );

  const actionable = payload.rows
    .filter(
      (r): r is AuditRow & { verdict: Verdict } =>
        (r.verdict === 'Alarming' || r.verdict === 'Concerning') && !isInactive(r)
    )
    .filter(inRole)
    .sort((a, b) => {
      if (a.verdict === 'Alarming' && b.verdict !== 'Alarming') return -1;
      if (b.verdict === 'Alarming' && a.verdict !== 'Alarming') return 1;
      return 0;
    });

  // The second column. This tab previously showed flagged findings ONLY, so a mission-critical
  // question nobody had answered yet was invisible here — it is work, just not a finding
  // (Harry 2026-09-09).
  const mcUnansweredIds = new Set(payload.missionCriticalUnansweredIds ?? []);
  const needsInput = payload.rows.filter(r => mcUnansweredIds.has(r.id)).filter(inRole);

  const alarming = actionable.filter(r => r.verdict === 'Alarming').length;
  const concerning = actionable.filter(r => r.verdict === 'Concerning').length;
  const resolvedCount = actionable.filter(r => resolved.has(r.id)).length;

  if (actionable.length === 0 && needsInput.length === 0) {
    return (
      <div className='actions-tab'>
        <RoleFilter value={role} onChange={setRole} />
        <div className='actions-empty'>
          {role === null
            ? 'Nothing flagged for investigation in this run, and every mission-critical question is answered. Re-run the audit to refresh against the latest data.'
            : 'Nothing outstanding for this role. Clear the role filter to see the whole account.'}
        </div>
      </div>
    );
  }

  return (
    <div className='actions-tab'>
      <RoleFilter value={role} onChange={setRole} />
      <div className='actions-header'>
        <div className='actions-counts'>
          {alarming > 0 && (
            <span className='count-pill alarming'>
              {alarming} {VerdictLabel.Alarming}
            </span>
          )}
          {concerning > 0 && (
            <span className='count-pill concerning'>
              {concerning} {VerdictLabel.Concerning}
            </span>
          )}
          {needsInput.length > 0 && (
            <span className='count-pill needs-input'>{needsInput.length} need your input</span>
          )}
        </div>
        <div className='actions-resolved'>
          {resolvedCount}/{actionable.length} addressed
        </div>
      </div>

      {/* Two columns (Harry 2026-09-09): findings to investigate on the left, questions awaiting a
          human answer on the right. Both are work, but they are different work. */}
      <div className='actions-cols'>
        <div className='actions-col'>
          <div className='actions-col-head'>
            Flagged for investigation
            <span className='actions-col-count'>
              {actionable.length} finding{actionable.length === 1 ? '' : 's'}, most severe first
            </span>
          </div>
          {actionable.length === 0 ? (
            <p className='actions-col-empty'>Nothing flagged for this role.</p>
          ) : (
            <div className='actions-list'>
              {actionable.map(row => (
                <ActionItem
                  key={row.id}
                  row={row}
                  resolved={resolved.has(row.id)}
                  onToggle={toggle}
                  onGoToRow={onGoToRow}
                />
              ))}
            </div>
          )}
        </div>

        <div className='actions-col'>
          <div className='actions-col-head'>
            Needs your input
            <span className='actions-col-count'>
              {needsInput.length} mission-critical question
              {needsInput.length === 1 ? '' : 's'} with no answer yet
            </span>
          </div>
          {needsInput.length === 0 ? (
            <p className='actions-col-empty'>
              Every mission-critical question is answered for this role.
            </p>
          ) : (
            <div className='actions-list'>
              {needsInput.map(row => (
                <InteractiveDiv
                  key={row.id}
                  className='needs-input-item'
                  onClick={() => onGoToRow(row)}
                >
                  <span className='needs-input-question'>{row.question}</span>
                  <span className='needs-input-loc'>
                    {AuditCategoryShortLabel[row.category]} › {row.topic}
                  </span>
                </InteractiveDiv>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface ActionItemProps {
  row: AuditRow & { verdict: Verdict };
  resolved: boolean;
  onToggle: (id: number) => void;
  onGoToRow: (row: AuditRow) => void;
}

const ActionItem = ({ row, resolved, onToggle, onGoToRow }: ActionItemProps) => (
  <div className={cn('action-item', VerdictClass[row.verdict], { 'is-resolved': resolved })}>
    <div className='action-check'>
      <Checkbox checked={resolved} onChange={() => onToggle(row.id)} />
    </div>
    <div className='action-body'>
      <div className='action-meta'>
        <span className={cn('action-severity', VerdictClass[row.verdict])}>
          {VerdictLabel[row.verdict]}
        </span>
        <span className='action-category'>{row.category}</span>
      </div>
      <InteractiveDiv
        className='action-question'
        title='Go to this row'
        onClick={() => onGoToRow(row)}
      >
        {row.question}
      </InteractiveDiv>
      {row.observedSummary && <div className='action-observed'>{row.observedSummary}</div>}
      {row.expectation && (
        <div className='action-target'>
          <span className='action-target-label'>Target: </span>
          {row.expectation}
        </div>
      )}
      {row.recommendation && (
        <div className='action-reco'>
          <span className='action-reco-label'>Next step: </span>
          {row.recommendation}
        </div>
      )}
    </div>
  </div>
);

// ── Category tab ────────────────────────────────────────────────────────────

// Frames each category as a distinct KIND of audit (Shane's gap taxonomy) so the ~128 questions don't
// read as one flat list: what a gap here means, who owns it, how often to run it, what to do with a
// finding. Grounded in the 2026-08 reviewer convergence (Shane/Liat/Luke/Corey).
const CategoryFrameHeader = ({ category }: { category: AuditRow['category'] }) => {
  const frame = CATEGORY_FRAME[category];
  if (!frame) return undefined;
  return (
    <CollapsibleSection
      className='category-frame'
      title={frame.gapType}
      summary={
        <span className='category-frame-roles'>
          {rolesForCategory(category).map(role => (
            <span key={role} className='role-chip'>
              {AuditRoleLabel[role]}
            </span>
          ))}
        </span>
      }
    >
      <p className='category-frame-purpose'>{frame.purpose}</p>
      <dl className='category-frame-meta'>
        <div className='category-frame-meta-item'>
          <dt>Re-run cadence</dt>
          <dd>{frame.cadence}</dd>
        </div>
        <div className='category-frame-meta-item'>
          <dt>What to do</dt>
          <dd>{frame.action}</dd>
        </div>
      </dl>
    </CollapsibleSection>
  );
};

interface QuestionsTabProps {
  payload: AuditPayload;
  onSaveAnswer: (
    id: number,
    verdict: Verdict | undefined,
    answer: string,
    extras?: SaveAnswerExtras
  ) => void;
  onRevertToAuto: (id: number) => void;
  onSaveOverride: (id: number, notApplicable: boolean, reason?: string) => void;
  scrollTargetRowId: number | null;
  onShowDetail: (row: AuditRow) => void;
}

// The four category tabs, consolidated (Harry 2026-09-09). Two columns, matching Needs attention so
// both working tabs read the same way:
//   LEFT  — mission-critical, always visible and sticky, each row labelled with the section it lives
//           in (it is lifted out of its group, so without that label it floats context-free).
//   RIGHT — everything else, as collapsed groups whose sub-sections are themselves collapsed. The
//           point is a clean page with the detail expandable rather than always present.
//
// Groupable by CATEGORY or by THEME. Themes cut across all four categories (Targeting Strategy spans
// three, Channel Mix carries 24 questions), and that cut was previously visible only as dead-end
// cards on Overview.
const QuestionsTab = ({
  payload,
  onSaveAnswer,
  onRevertToAuto,
  onSaveOverride,
  scrollTargetRowId,
  onShowDetail
}: QuestionsTabProps) => {
  const [role, setRole] = useState<AuditRoleType | null>(null);
  const [groupBy, setGroupBy] = useState<'category' | 'theme'>('category');

  // Unlike the Overview role filter, this one narrows the QUESTION LISTS themselves. Role derives
  // from category, so it is really "show me the categories my seat owns".
  const rows = useMemo(
    () =>
      role === null
        ? payload.rows
        : payload.rows.filter(r => ROLES_BY_CATEGORY[r.category]?.includes(role)),
    [payload.rows, role]
  );

  // N/A rows are excluded from scoring, but reviewers (Corey/Shane, 2026-08) found them too easy to
  // miss when hidden by default — an auto-N/A that is actually wrong (a section the client really
  // runs) is exactly what an audit should catch. Shown by default with a verification nudge; the
  // toggle still lets someone collapse them to focus on the scored queue. Carried over from the
  // category tabs this replaced, tab-wide instead of per category.
  const [showNa, setShowNa] = useState(true);
  const naCount = useMemo(() => rows.filter(r => r.notApplicable).length, [rows]);
  const visibleRows = useMemo(
    () => (showNa ? rows : rows.filter(r => !r.notApplicable)),
    [rows, showNa]
  );

  const mcRows = useMemo(() => visibleRows.filter(r => r.missionCritical), [visibleRows]);
  const restRows = useMemo(() => visibleRows.filter(r => !r.missionCritical), [visibleRows]);

  const groups = useMemo(() => {
    if (groupBy === 'theme') {
      const themes = payload.themes ?? [];
      const byId = new Map(restRows.map(r => [r.id, r]));
      const themed = new Set<number>();
      const out = themes.map(t => {
        const themeRows = t.questionIds
          .map(id => byId.get(id))
          .filter((r): r is AuditRow => r !== undefined);
        themeRows.forEach(r => themed.add(r.id));
        return { key: t.id, label: t.label, rows: themeRows };
      });
      // A row belonging to no theme would otherwise vanish from this tab entirely. The backend now
      // asserts full theme coverage, so this should stay empty — it is here so a future unassigned
      // row surfaces instead of disappearing.
      const orphans = restRows.filter(r => !themed.has(r.id));
      return orphans.length > 0
        ? [...out, { key: 'unthemed', label: 'Not in a theme', rows: orphans }]
        : out;
    }
    return CATEGORY_ORDER.map(c => ({
      key: c,
      label: AuditCategoryShortLabel[c],
      rows: restRows.filter(r => r.category === c)
    }));
  }, [groupBy, payload.themes, restRows]);

  const needsInput = (r: AuditRow): boolean => !r.verdict && !r.notApplicable;

  return (
    <div className='questions-tab'>
      <div className='questions-tab-controls'>
        <RoleFilter value={role} onChange={setRole} />
        <div className='group-by-switch'>
          <span className='group-by-label'>Group by</span>
          <Button
            size={Size.SM}
            appearance={groupBy === 'category' ? Appearance.FILLED : Appearance.OUTLINED}
            onClick={() => setGroupBy('category')}
          >
            Category
          </Button>
          <Button
            size={Size.SM}
            appearance={groupBy === 'theme' ? Appearance.FILLED : Appearance.OUTLINED}
            onClick={() => setGroupBy('theme')}
          >
            Theme
          </Button>
        </div>
      </div>

      {naCount > 0 && (
        <div className='questions-na-controls'>
          <Checkbox
            className='show-na-toggle'
            label={`Show N/A (${naCount})`}
            title='N/A rows are excluded from the queue — hide them to focus on the scored queue'
            checked={showNa}
            onChange={e => setShowNa(e.target.checked)}
          />
          {showNa && (
            <p className='na-verify-note'>
              N/A rows are auto-excluded from scoring — skim them to confirm each was marked
              correctly, and re-enable any the client actually runs.
            </p>
          )}
        </div>
      )}

      <div className='questions-cols'>
        <div className='questions-col questions-col--mc'>
          <div className='questions-col-head'>
            Mission-critical
            <span className='questions-col-count'>
              {mcRows.length} question{mcRows.length === 1 ? '' : 's'} · always visible
            </span>
          </div>
          {mcRows.length === 0 ? (
            <p className='questions-empty'>No mission-critical questions for this role.</p>
          ) : (
            mcRows.map(row => (
              // The category › topic breadcrumb used to sit above each row here; it is part of the
              // folded row itself now, so the wrapper only supplies spacing.
              <div key={row.id} className='mc-row-wrap'>
                <AuditRowView
                  row={row}
                  onSaveAnswer={onSaveAnswer}
                  onRevertToAuto={onRevertToAuto}
                  onSaveOverride={onSaveOverride}
                  isScrollTarget={scrollTargetRowId === row.id}
                  onShowDetail={onShowDetail}
                />
              </div>
            ))
          )}
        </div>

        <div className='questions-col questions-col--rest'>
          <div className='questions-col-head'>
            Everything else
            <span className='questions-col-count'>
              {restRows.length} question{restRows.length === 1 ? '' : 's'} · grouped by {groupBy}
            </span>
          </div>
          {groups
            .filter(g => g.rows.length > 0)
            .map(g => {
              const flagged = flaggedCount(g.rows);
              const unanswered = g.rows.filter(needsInput).length;
              const holdsTarget =
                scrollTargetRowId !== null && g.rows.some(r => r.id === scrollTargetRowId);
              return (
                <CollapsibleSection
                  key={g.key}
                  className='question-group'
                  title={g.label}
                  // Counts on the header so the reader can tell what is inside before opening it.
                  summary={`${g.rows.length}${flagged > 0 ? ` · ${flagged} flagged` : ''}${
                    unanswered > 0 ? ` · ${unanswered} need input` : ''
                  }`}
                  // Collapsed by default — that is the point of the change. A deep-link from another
                  // tab still forces the group open so the target row is reachable.
                  forceOpen={holdsTarget}
                >
                  {/* The category frame (gap type / owning roles / cadence) lived on the old
                      per-category tabs. It belongs inside the segment now, not above the whole tab,
                      since one tab covers all four categories. */}
                  {groupBy === 'category' && (
                    <CategoryFrameHeader category={g.key as AuditRow['category']} />
                  )}
                  <SubGroups
                    rows={g.rows}
                    groupBy={groupBy}
                    onSaveAnswer={onSaveAnswer}
                    onRevertToAuto={onRevertToAuto}
                    onSaveOverride={onSaveOverride}
                    scrollTargetRowId={scrollTargetRowId}
                    onShowDetail={onShowDetail}
                  />
                </CollapsibleSection>
              );
            })}
        </div>
      </div>
    </div>
  );
};

interface SubGroupsProps {
  rows: AuditRow[];
  groupBy: 'category' | 'theme';
  onSaveAnswer: (
    id: number,
    verdict: Verdict | undefined,
    answer: string,
    extras?: SaveAnswerExtras
  ) => void;
  onRevertToAuto: (id: number) => void;
  onSaveOverride: (id: number, notApplicable: boolean, reason?: string) => void;
  scrollTargetRowId: number | null;
  onShowDetail: (row: AuditRow) => void;
}

// The third level of disclosure. Grouping by category sub-divides by TOPIC; grouping by theme
// sub-divides by CATEGORY, since a theme's rows come from several categories and that is the useful
// split once you are inside one.
const SubGroups = ({ rows, groupBy, ...handlers }: SubGroupsProps) => {
  const subs = useMemo(() => {
    const order: string[] = [];
    const bucket = new Map<string, AuditRow[]>();
    for (const row of rows) {
      const key = groupBy === 'theme' ? AuditCategoryShortLabel[row.category] : row.topic;
      if (!bucket.has(key)) {
        bucket.set(key, []);
        order.push(key);
      }
      bucket.get(key)!.push(row);
    }
    return order.map(key => ({ key, rows: bucket.get(key)! }));
  }, [rows, groupBy]);

  return (
    <>
      {subs.map(({ key, rows: subRows }) => (
        <TopicSection
          key={key}
          topic={key}
          rows={subRows}
          // Collapsed unless it holds a flag, so opening a group does not dump every row at once.
          defaultOpen={flaggedCount(subRows) > 0}
          onSaveAnswer={handlers.onSaveAnswer}
          onRevertToAuto={handlers.onRevertToAuto}
          onSaveOverride={handlers.onSaveOverride}
          scrollTargetRowId={handlers.scrollTargetRowId}
          onShowDetail={handlers.onShowDetail}
        />
      ))}
    </>
  );
};

interface TopicSectionProps {
  topic: string;
  rows: AuditRow[];
  defaultOpen: boolean;
  onSaveAnswer: (
    id: number,
    verdict: Verdict | undefined,
    answer: string,
    extras?: SaveAnswerExtras
  ) => void;
  onRevertToAuto: (id: number) => void;
  onSaveOverride: (id: number, notApplicable: boolean, reason?: string) => void;
  scrollTargetRowId: number | null;
  onShowDetail: (row: AuditRow) => void;
}

const TopicSection = ({
  topic,
  rows,
  defaultOpen,
  onSaveAnswer,
  onRevertToAuto,
  onSaveOverride,
  scrollTargetRowId,
  onShowDetail
}: TopicSectionProps) => {
  const flagged = flaggedCount(rows);
  const containsTarget = scrollTargetRowId != null && rows.some(r => r.id === scrollTargetRowId);
  return (
    <CollapsibleSection
      className='topic-section'
      title={topic}
      defaultOpen={defaultOpen}
      forceOpen={containsTarget}
      summary={
        <>
          <span className='topic-count'>{conditionallyPluralizeWithCount(rows.length, 'row')}</span>
          {flagged > 0 && <span className='topic-flagged'>{flagged} flagged</span>}
        </>
      }
    >
      {rows.map(row => (
        <AuditRowView
          key={row.id}
          row={row}
          onSaveAnswer={onSaveAnswer}
          onRevertToAuto={onRevertToAuto}
          onSaveOverride={onSaveOverride}
          isScrollTarget={scrollTargetRowId === row.id}
          onShowDetail={onShowDetail}
        />
      ))}
    </CollapsibleSection>
  );
};

interface AuditRowViewProps {
  row: AuditRow;
  onSaveAnswer: (
    id: number,
    verdict: Verdict | undefined,
    answer: string,
    extras?: SaveAnswerExtras
  ) => void;
  onRevertToAuto: (id: number) => void;
  onSaveOverride: (id: number, notApplicable: boolean, reason?: string) => void;
  isScrollTarget: boolean;
  onShowDetail: (row: AuditRow) => void;
}

const AuditRowView = ({
  row,
  onSaveAnswer,
  onRevertToAuto,
  onSaveOverride,
  isScrollTarget,
  onShowDetail
}: AuditRowViewProps) => {
  const isAutoTier = row.automationTier === AutomationTier.AUTO;
  const isAutoAnswered = isAutoTier && row.origin === Origin.AUTO;
  const [overriding, setOverriding] = useState(false);
  // Folded by default (Harry 2026-09-09): the list reads as question + state + location, and
  // everything else — tags, N/A, guidance, the editor — appears on click. Before this, every row
  // rendered its full editor at all times, which is what made the page hard to read.
  const [expanded, setExpanded] = useState(false);
  const flag = rowFlag(row);
  // CAB-5 deep-link: when this row is the navigation target, scroll it into view and flash a
  // highlight. useLayoutEffect so the scroll happens before paint (no visible jump).
  const rowRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (isScrollTarget && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [isScrollTarget]);
  // A deep-link from Needs attention has to land on an OPEN row, or the jump appears to do nothing.
  useEffect(() => {
    if (isScrollTarget) {
      setExpanded(true);
    }
  }, [isScrollTarget]);
  // Auto-answered rows show a read-only foot with an "Override →" affordance; every other row
  // (unanswered, non-auto tier, or already overridden) gets the manual editor directly, so a row is
  // never a dead end — matching the mission-critical banner's promise ("auto-derived or manual").
  const showEditor = !row.notApplicable && (!isAutoAnswered || row.overrodeAuto || overriding);
  const showAutoFoot = !row.notApplicable && isAutoAnswered;
  const isOverride = isAutoAnswered || Boolean(row.overrodeAuto);
  // Manual / connector-degraded rows carry their verdict guidance in `expectation`. Parsed once: the composed "Good: … · Concerning: …" lines, plus any un-prefixed intro text. A row
  // whose guidance is a plain sentence (an auto rule's own expectation) has intro and no items, and
  // that is worth showing too — so the check covers both.
  const guidance = parseManualGuidance(row.expectation);
  const hasGuidance = guidance.items.length > 0 || guidance.intro.length > 0;

  return (
    <div
      ref={rowRef}
      className={cn('audit-row', {
        'is-na': row.notApplicable,
        'is-degraded': row.degradedReason,
        'is-highlighted': isScrollTarget,
        'is-expanded': expanded
      })}
    >
      {/* Folded header — the whole row is the toggle. Question, then one subtext line carrying the
          state flag and where the question lives. */}
      <InteractiveDiv
        className='row-fold'
        aria-expanded={expanded}
        onClick={() => setExpanded(open => !open)}
      >
        <span className='row-question'>{row.question}</span>
        <span className='row-subline'>
          <span className={cn('row-flag', flag.className)}>{flag.label}</span>
          <span className='row-loc'>
            {AuditCategoryShortLabel[row.category]} › {row.topic}
          </span>
        </span>
      </InteractiveDiv>

      {expanded && (
        <div className='row-body'>
          <div className='row-tags'>
            {row.missionCritical && (
              <span className='mc-tag' title='Mission-critical — must be answered'>
                Mission-critical
              </span>
            )}
            {isAutoTier && row.doctrineStatus && <DoctrineBadge status={row.doctrineStatus} />}
            {row.sourceConfidence && row.sourceConfidence !== 'measured' && (
              <SourceConfidenceBadge confidence={row.sourceConfidence} />
            )}
            {row.degradedReason && (
              <span className='degraded-badge' title={row.degradedReason}>
                Connector unavailable
              </span>
            )}
            {row.overrodeAuto && (
              <span
                className='override-badge'
                title='A manual answer replaced the auto-derived verdict'
              >
                Overrides auto
              </span>
            )}
            {row.disposition === 'accepted' && (
              <span
                className='accepted-badge'
                title='Accepted as a known, deliberate gap — excluded from scoring but kept visible'
              >
                Accepted
              </span>
            )}
            <TierTag tier={row.automationTier} origin={row.origin} />
            <span className='row-head-spacer' />
            <NaToggle
              notApplicable={Boolean(row.notApplicable)}
              onToggle={na => onSaveOverride(row.id, na)}
            />
          </div>

          {row.notApplicable ? (
            <NaFoot row={row} onSaveOverride={onSaveOverride} />
          ) : (
            <>
              {row.disposition === 'accepted' && (row.revisitAt || row.nextStep) && (
                <div className='accepted-foot'>
                  {row.revisitAt && (
                    <span className='accepted-revisit'>Revisit: {row.revisitAt}</span>
                  )}
                  {row.nextStep && (
                    <span className='accepted-next-step'>Next step: {row.nextStep}</span>
                  )}
                </div>
              )}
              {showAutoFoot && (
                <div className='auto-foot'>
                  {row.observedSummary && (
                    <span className='row-observed'>{row.observedSummary}</span>
                  )}
                  <InteractiveDiv className='row-details-link' onClick={() => onShowDetail(row)}>
                    Why this verdict →
                  </InteractiveDiv>
                  {row.notes && <span className='row-note'>Note: {row.notes}</span>}
                  {!showEditor && (
                    <>
                      <InteractiveDiv
                        className='row-override-link'
                        onClick={() => setOverriding(true)}
                      >
                        Override →
                      </InteractiveDiv>
                      <InteractiveDiv className='row-note-link' onClick={() => setOverriding(true)}>
                        {row.notes ? 'Edit note →' : 'Add note →'}
                      </InteractiveDiv>
                    </>
                  )}
                </div>
              )}
              {showEditor && (
                <>
                  {row.degradedReason && (
                    <div className='manual-guide-foot'>
                      <span className='degraded-note'>{row.degradedReason}</span>
                    </div>
                  )}
                  {/* Guidance is rendered INLINE now that the row folds (Harry 2026-09-09) — it used to
                  be a "Verdict guidance →" link opening a modal on top of the row you had just
                  opened. Same component the modal uses, so there is one source of this markup. */}
                  {hasGuidance && <ManualGuidanceSection guidance={guidance} />}
                  <ManualEditor
                    row={row}
                    onSaveAnswer={onSaveAnswer}
                    onRevertToAuto={onRevertToAuto}
                    autoOverride={isOverride}
                    onCancel={
                      overriding && !row.overrodeAuto ? () => setOverriding(false) : undefined
                    }
                  />
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Not-applicable control ──────────────────────────────────────────────────

interface NaToggleProps {
  notApplicable: boolean;
  onToggle: (notApplicable: boolean) => void;
}

const NaToggle = ({ notApplicable, onToggle }: NaToggleProps) => (
  <Checkbox
    className='na-toggle'
    label='N/A'
    title='Mark not applicable for this client — excluded from the queue'
    checked={notApplicable}
    onChange={e => onToggle(e.target.checked)}
  />
);

interface NaFootProps {
  row: AuditRow;
  onSaveOverride: (id: number, notApplicable: boolean, reason?: string) => void;
}

const NaFoot = ({ row, onSaveOverride }: NaFootProps) => {
  const [reason, setReason] = useState(row.naReason ?? '');

  useEffect(() => {
    setReason(row.naReason ?? '');
  }, [row.naReason]);

  const dirty = reason !== (row.naReason ?? '');

  return (
    <div className='na-foot'>
      <span className='na-label'>Excluded from queue</span>
      <Form.Control
        className='na-reason'
        placeholder='Why is this N/A for this client? (optional)'
        value={reason}
        onChange={e => setReason(e.target.value)}
      />
      <Button
        size={Size.SM}
        appearance={Appearance.OUTLINED}
        disabled={!dirty}
        onClick={() => onSaveOverride(row.id, true, reason)}
      >
        Save reason
      </Button>
    </div>
  );
};

// ── Auto-row detail modal ─────────────────────────────────────────────────────

interface RowDetailModalProps {
  row: AuditRow;
  onClose: () => void;
}

const RowDetailModal = ({ row, onClose }: RowDetailModalProps) => {
  const isAutoTier = row.automationTier === AutomationTier.AUTO;
  const guidance = parseManualGuidance(row.expectation);
  const hasGuidance = guidance.items.length > 0;

  return (
    <InteractiveDiv className='detail-backdrop' onClick={onClose}>
      <InteractiveDiv className='detail-panel' onClick={e => e.stopPropagation()}>
        <div className='detail-head'>
          {row.insufficientData && (
            <span
              className='no-data'
              title='Not enough information to answer this from available data'
            >
              Not enough information
            </span>
          )}
          {!row.insufficientData && row.verdict && <VerdictPill verdict={row.verdict} />}
          <span className='detail-id'>#{row.id}</span>
          {isAutoTier && row.doctrineStatus && <DoctrineBadge status={row.doctrineStatus} />}
          {row.sourceConfidence && <SourceConfidenceBadge confidence={row.sourceConfidence} />}
          <span className='detail-head-spacer' />
          <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={onClose}>
            Close
          </Button>
        </div>
        <div className='detail-question'>{row.question}</div>

        {row.degradedReason && (
          <DetailSection label='Why this is manual' body={row.degradedReason} />
        )}
        <DetailSection label='What we observed' body={row.observedSummary} />
        <DetailSection label='Why this verdict' body={row.rationale} />
        {row.evidenceUrls && row.evidenceUrls.length > 0 && (
          <EvidenceSourcesSection urls={row.evidenceUrls} />
        )}
        {hasGuidance ? (
          <ManualGuidanceSection guidance={guidance} />
        ) : (
          <DetailSection label='What "Good" looks like' body={row.expectation} />
        )}
        <DetailSection label='Recommended next step' body={row.recommendation} />

        {(row.basis || row.humanContext || row.calibration || isAutoTier) && (
          <details className='detail-methodology'>
            <summary>Audit methodology</summary>
            <DetailSection label='Data source' body={row.basis} />
            <DetailSection label='Context' body={row.humanContext} />
            {isAutoTier &&
              (row.citation ? (
                <DetailSection
                  label='Pinned to'
                  body={`${row.citation.source} › ${row.citation.section}`}
                  quote={row.citation.quote}
                />
              ) : (
                <DetailSection
                  label='Doctrine'
                  body='Unpinned — the verdict shape is doctrine-aligned but not yet pinned to a verbatim citation. See the Guidance tab.'
                />
              ))}
            {row.calibration && <DetailSection label='Calibration' body={row.calibration} />}
          </details>
        )}
      </InteractiveDiv>
    </InteractiveDiv>
  );
};

// Verdict guidance for manual / connector-degraded rows: the per-verdict "what each looks like" lines
// (reframed labels), parsed from the composed `expectation`. Mirrors the auto "why this verdict" modal.
const ManualGuidanceSection = ({ guidance }: { guidance: ParsedGuidance }) => (
  <div className='detail-section'>
    <div className='detail-label'>Verdict guidance</div>
    {guidance.intro.map(line => (
      <div key={line} className='detail-body'>
        {line}
      </div>
    ))}
    <div className='manual-guidance-items'>
      {guidance.items.map(item => (
        <div key={item.verdict} className={cn('manual-guidance-item', VerdictClass[item.verdict])}>
          <span className='manual-guidance-verdict'>{item.label}</span>
          <span className='manual-guidance-text'>{item.text}</span>
        </div>
      ))}
    </div>
  </div>
);

// Web sources behind an AI-derived verdict (e.g. competitor research). Rendered so a skeptic can
// check the actual evidence the verdict rests on, matching the "confirm before relying" framing.
const EvidenceSourcesSection = ({ urls }: { urls: string[] }) => (
  <div className='detail-section'>
    <div className='detail-label'>Sources</div>
    {urls.map(url => (
      <EvidenceSource key={url} url={url} />
    ))}
  </div>
);

// Renders one evidence URL. Only http(s) becomes a clickable link; anything else (javascript:,
// data:, a malformed string, etc.) is shown as inert text so an untrusted AI-supplied URL can't
// execute script or navigate on click.
const EvidenceSource = ({ url }: { url: string }) => {
  if (!isSafeHttpUrl(url)) return <div className='detail-source'>{url}</div>;
  return (
    <div className='detail-source'>
      <a href={url} target='_blank' rel='noopener noreferrer'>
        {url}
      </a>
    </div>
  );
};

const DetailSection = ({
  label,
  body,
  quote
}: {
  label: string;
  body?: string;
  quote?: string;
}) => {
  if (!body) return null;
  return (
    <div className='detail-section'>
      <div className='detail-label'>{label}</div>
      <div className='detail-body'>{body}</div>
      {quote && <div className='detail-quote'>&ldquo;{quote}&rdquo;</div>}
    </div>
  );
};

// ── Manual editor ─────────────────────────────────────────────────────────────

interface ManualEditorProps {
  row: AuditRow;
  onSaveAnswer: (
    id: number,
    verdict: Verdict | undefined,
    answer: string,
    extras?: SaveAnswerExtras
  ) => void;
  onRevertToAuto: (id: number) => void;
  autoOverride?: boolean;
  onCancel?: () => void;
}

const ManualEditor = ({
  row,
  onSaveAnswer,
  onRevertToAuto,
  autoOverride = false,
  onCancel
}: ManualEditorProps) => {
  // Start the verdict picker unselected on any row that isn't already a manual override, so a bare
  // note saves as a note-without-override (F31) and picking a verdict is what turns it into an
  // override. An already-overridden row (origin manual) prefills its saved verdict for editing.
  const isManualOverride = row.origin === Origin.MANUAL;
  const initialVerdict = isManualOverride ? row.verdict : undefined;
  // Prefill the note from the override rationale (manual) or the note-only text (row.notes). For an
  // accepted row the rationale also lives in row.notes.
  const savedAnswer = isManualOverride ? (row.rationale ?? '') : (row.notes ?? '');
  // Accepted disposition (known, deliberate gap): excluded from scoring, kept visible. Prefill its
  // rationale/revisit/next-step so re-opening the editor edits the saved values.
  const isAcceptedRow = row.disposition === 'accepted';
  const savedRevisit = row.revisitAt ?? '';
  const savedNextStep = row.nextStep ?? '';
  const [verdict, setVerdict] = useState<Verdict | undefined>(initialVerdict);
  const [answer, setAnswer] = useState(savedAnswer);
  const [accepted, setAccepted] = useState(isAcceptedRow);
  const [revisitAt, setRevisitAt] = useState(savedRevisit);
  const [nextStep, setNextStep] = useState(savedNextStep);

  useEffect(() => {
    setVerdict(initialVerdict);
    setAnswer(savedAnswer);
    setAccepted(isAcceptedRow);
    setRevisitAt(savedRevisit);
    setNextStep(savedNextStep);
  }, [initialVerdict, savedAnswer, isAcceptedRow, savedRevisit, savedNextStep]);

  const dirty =
    accepted !== isAcceptedRow ||
    verdict !== initialVerdict ||
    answer !== savedAnswer ||
    revisitAt !== savedRevisit ||
    nextStep !== savedNextStep;
  // Saveable once dirty: accepting requires a rationale; otherwise a verdict (override) or a note.
  const canSave =
    dirty && (accepted ? answer.trim().length > 0 : Boolean(verdict) || answer.trim().length > 0);
  // Something is persisted (override / note / accepted) that can be reverted to auto (F16).
  const canRevert =
    isManualOverride || Boolean(row.overrodeAuto) || Boolean(row.notes) || isAcceptedRow;
  const isPersisted = isManualOverride || Boolean(row.notes) || isAcceptedRow;

  const handleSave = () => {
    if (accepted) {
      onSaveAnswer(row.id, undefined, answer, {
        disposition: 'accepted',
        revisitAt: revisitAt || undefined,
        nextStep: nextStep || undefined
      });
    } else {
      // No extras → the backend derives disposition from the current answer, clearing any prior accept.
      onSaveAnswer(row.id, verdict, answer);
    }
  };

  const acceptedHint = accepted ? 'Accepted:' : 'Note:';
  const hintText = !accepted && verdict ? 'Override:' : acceptedHint;

  const notePlaceholder = verdict ? 'Add a note…' : 'Add a note, or pick a verdict to override…';
  const answerPlaceholder = accepted
    ? 'Why is this an accepted, known gap? (required)'
    : notePlaceholder;

  return (
    <div className={cn('manual-editor', { 'is-override': autoOverride, 'is-accepting': accepted })}>
      {autoOverride && <span className='override-hint'>{hintText}</span>}
      <Checkbox
        className='accept-toggle'
        label='Accept as known gap'
        title='Close this as a known, deliberate gap — excluded from scoring but kept visible.'
        checked={accepted}
        onChange={e => setAccepted(e.target.checked)}
      />
      {!accepted && (
        <div className='verdict-picker'>
          {VERDICT_OPTIONS.map(v => (
            <InteractiveDiv
              key={v}
              className={cn('verdict-choice', VerdictClass[v], { selected: verdict === v })}
              onClick={() => setVerdict(verdict === v ? undefined : v)}
            >
              {VerdictLabel[v]}
            </InteractiveDiv>
          ))}
        </div>
      )}
      <Form.Control
        as='textarea'
        rows={1}
        className='answer-input'
        placeholder={answerPlaceholder}
        value={answer}
        onChange={e => setAnswer(e.target.value)}
      />
      {accepted && (
        <div className='accept-fields'>
          <div className='accept-field'>
            <span className='accept-field-label'>Revisit</span>
            <Form.Control
              type='date'
              aria-label='Revisit date'
              value={revisitAt}
              onChange={e => setRevisitAt(e.target.value)}
            />
          </div>
          <Form.Control
            className='accept-next-step'
            placeholder='Next step (optional)'
            aria-label='Next step'
            value={nextStep}
            onChange={e => setNextStep(e.target.value)}
          />
        </div>
      )}
      <Button size={Size.SM} disabled={!canSave} onClick={handleSave}>
        {!dirty && isPersisted ? 'Saved' : 'Save'}
      </Button>
      {canRevert && (
        <Button
          size={Size.SM}
          appearance={Appearance.OUTLINED}
          onClick={() => onRevertToAuto(row.id)}
        >
          Revert to auto
        </Button>
      )}
      {onCancel && (
        <Button size={Size.SM} appearance={Appearance.OUTLINED} onClick={onCancel}>
          Cancel
        </Button>
      )}
    </div>
  );
};

// ── Guidance / doctrine tab ────────────────────────────────────────────────────

const DOCTRINE_ORDER: DoctrineStatus[] = ['THIN', 'GAP', 'PINNED'];
const DOCTRINE_GROUP_BLURB: Record<DoctrineStatus, string> = {
  THIN: 'Verdict logic is doctrine-aligned but not yet pinned to a verbatim Tinuiti Way citation. These need a citation to firm up.',
  GAP: 'No doctrine encoded yet — the verdict is a placeholder awaiting a Tinuiti Way position.',
  PINNED: 'Backed by a verbatim Tinuiti Way citation. Solid.'
};

interface GuidanceTabProps {
  doctrine: DoctrineEntry[] | null;
  error: string;
  onRetry: () => void;
  onSaveGuidance: (target: { questionId: number }, guidance: GuidanceRecordInput) => Promise<void>;
}

const GuidanceTab = ({ doctrine, error, onRetry, onSaveGuidance }: GuidanceTabProps) => {
  // Clearing doctrineError re-arms the load effect's guard so the doctrine fetch retries;
  // without this the tab stays permanently errored after a single transient fetch failure.
  if (error) return <ServiceError error={error} onRetry={onRetry} />;
  if (!doctrine) return <Spinner />;
  const groups = DOCTRINE_ORDER.map(status => ({
    status,
    entries: doctrine.filter(d => d.doctrineStatus === status).sort((a, b) => a.id - b.id)
  })).filter(g => g.entries.length > 0);

  return (
    <div className='guidance-tab'>
      <div className='guidance-intro'>
        Doctrine coverage of the rule catalog — <strong>not client-specific</strong>. This is for
        whoever maintains the audit&apos;s Tinuiti Way encoding: it shows which automated verdicts
        are solidly pinned vs. which remain unpinned and need a citation. Edits here are{' '}
        <strong>global</strong> — they change the audit for every client.
      </div>
      {groups.map(group => (
        <DoctrineGroup
          key={group.status}
          status={group.status}
          entries={group.entries}
          onSaveGuidance={onSaveGuidance}
        />
      ))}
    </div>
  );
};

interface DoctrineGroupProps {
  status: DoctrineStatus;
  entries: DoctrineEntry[];
  onSaveGuidance: (target: { questionId: number }, guidance: GuidanceRecordInput) => Promise<void>;
}

const DoctrineGroup = ({ status, entries, onSaveGuidance }: DoctrineGroupProps) => (
  <div className='doctrine-group'>
    <div className='doctrine-group-head'>
      <DoctrineBadge status={status} />
      <span className='doctrine-group-count'>{entries.length}</span>
      <span className='doctrine-group-blurb'>{DOCTRINE_GROUP_BLURB[status]}</span>
    </div>
    {entries.map(entry => (
      <DoctrineRow key={entry.id} entry={entry} onSaveGuidance={onSaveGuidance} />
    ))}
  </div>
);

interface DoctrineRowProps {
  entry: DoctrineEntry;
  onSaveGuidance: (target: { questionId: number }, guidance: GuidanceRecordInput) => Promise<void>;
}

const DoctrineRow = ({ entry, onSaveGuidance }: DoctrineRowProps) => {
  const [editing, setEditing] = useState(false);

  return (
    <div className='doctrine-row'>
      <div className='doctrine-row-head'>
        <span className='row-id'>#{entry.id}</span>
        <span className='row-question'>{entry.question}</span>
        {entry.edited && (
          <span className='edited-badge' title='This guidance has been hand-edited'>
            Edited
          </span>
        )}
        <span className='doctrine-cat'>{entry.category}</span>
        <InteractiveDiv className='doctrine-edit-link' onClick={() => setEditing(v => !v)}>
          {editing ? 'Cancel' : 'Edit'}
        </InteractiveDiv>
      </div>
      {entry.citation ? (
        <div className='doctrine-cite'>
          {entry.citation.source} › {entry.citation.section} — &ldquo;{entry.citation.quote}&rdquo;
        </div>
      ) : (
        entry.basis && <div className='doctrine-basis'>{entry.basis}</div>
      )}
      {entry.expectation && <div className='doctrine-expectation'>{entry.expectation}</div>}
      {entry.humanContext && (
        <div className='doctrine-context'>
          <span className='doctrine-context-label'>Context: </span>
          {entry.humanContext}
        </div>
      )}
      {editing && (
        <GuidanceEditor entry={entry} onSave={onSaveGuidance} onClose={() => setEditing(false)} />
      )}
    </div>
  );
};

interface GuidanceEditorProps {
  entry: DoctrineEntry;
  onSave: (target: { questionId: number }, guidance: GuidanceRecordInput) => Promise<void>;
  onClose: () => void;
}

const GuidanceEditor = ({ entry, onSave, onClose }: GuidanceEditorProps) => {
  const [basis, setBasis] = useState(entry.basis ?? '');
  const [expectation, setExpectation] = useState(entry.expectation ?? '');
  const [humanContext, setHumanContext] = useState(entry.humanContext ?? '');
  const [calibration, setCalibration] = useState(entry.calibration ?? '');
  const [good, setGood] = useState(entry.rationale?.Good ?? '');
  const [concerning, setConcerning] = useState(entry.rationale?.Concerning ?? '');
  const [alarming, setAlarming] = useState(entry.rationale?.Alarming ?? '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const showError = useSetError();

  const dirty =
    basis !== (entry.basis ?? '') ||
    expectation !== (entry.expectation ?? '') ||
    humanContext !== (entry.humanContext ?? '') ||
    calibration !== (entry.calibration ?? '') ||
    good !== (entry.rationale?.Good ?? '') ||
    concerning !== (entry.rationale?.Concerning ?? '') ||
    alarming !== (entry.rationale?.Alarming ?? '');

  const onSaveClick = async () => {
    // Trim-to-empty omits a field, which the backend treats as "revert to code default" (full
    // record replace + per-field fallback in buildRows); humanContext has no default so it clears.
    const rationale: Partial<Record<Verdict, string>> = {
      ...(good.trim() ? { Good: good } : {}),
      ...(concerning.trim() ? { Concerning: concerning } : {}),
      ...(alarming.trim() ? { Alarming: alarming } : {})
    };

    const guidance: GuidanceRecordInput = {
      ...(basis.trim() ? { basis } : {}),
      ...(expectation.trim() ? { expectation } : {}),
      ...(humanContext.trim() ? { humanContext } : {}),
      ...(calibration.trim() ? { calibration } : {}),
      ...(Object.keys(rationale).length > 0 ? { rationale } : {})
    };

    setSaving(true);
    setSaveError('');
    try {
      await onSave({ questionId: entry.id }, guidance);
      onClose();
    } catch (e) {
      // Keep the in-form message — the editor stays open, so a modal alone would hide the context
      // the user needs. The modal call is what reports a failed GLOBAL doctrine save.
      setSaveError(e.message);
      showError({ message: `Couldn't save guidance: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='guidance-editor'>
      <div className='guidance-editor-warning'>
        This guidance is <strong>global</strong>. Saving changes the audit&apos;s doctrine for{' '}
        <strong>every client</strong>, not just this one.
      </div>
      <GuidanceField
        label='Context (why this matters)'
        value={humanContext}
        onChange={setHumanContext}
      />
      <GuidanceField label='Basis' value={basis} onChange={setBasis} />
      <GuidanceField label='What "Good" looks like' value={expectation} onChange={setExpectation} />
      <GuidanceField label='Calibration' value={calibration} onChange={setCalibration} />
      <div className='guidance-rationale'>
        <div className='guidance-field-label'>Verdict rationale</div>
        <GuidanceField label='Good' value={good} onChange={setGood} compact />
        <GuidanceField label='Concerning' value={concerning} onChange={setConcerning} compact />
        <GuidanceField label='Alarming' value={alarming} onChange={setAlarming} compact />
      </div>
      {saveError && <div className='guidance-editor-error'>{saveError}</div>}
      <div className='guidance-editor-actions'>
        <Button size={Size.LG} appearance={Appearance.OUTLINED} onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button size={Size.LG} onClick={onSaveClick} loading={saving} disabled={!dirty || saving}>
          Save guidance
        </Button>
      </div>
    </div>
  );
};

interface GuidanceFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
}

const GuidanceField = ({ label, value, onChange, compact = false }: GuidanceFieldProps) => (
  <div className={cn('guidance-field', { compact })}>
    <div className='guidance-field-label'>{label}</div>
    <Form.Control
      as='textarea'
      rows={compact ? 1 : 2}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

// ── Small shared bits ─────────────────────────────────────────────────────────

// Space-tight pill shows the short verdict form with the full reframed label on hover (CA-9).
const VerdictPill = ({ verdict }: { verdict: Verdict }) => (
  <span className={cn('verdict-pill', VerdictClass[verdict])} title={VerdictLabel[verdict]}>
    {VerdictAbbrev[verdict]}
  </span>
);

const SourceConfidenceBadge = ({
  confidence
}: {
  confidence: NonNullable<AuditRow['sourceConfidence']>;
}) => (
  <span
    className={cn('source-confidence-badge', SourceConfidenceClass[confidence])}
    title={SourceConfidenceTitle[confidence]}
  >
    {SourceConfidenceLabel[confidence]}
  </span>
);

const DoctrineBadge = ({ status }: { status: DoctrineStatus }) => (
  <span
    className={cn('doctrine-badge', DoctrineClass[status])}
    title={`Doctrine: ${DoctrineLabel[status]}`}
  >
    {DoctrineLabel[status]}
  </span>
);

interface TierTagProps {
  tier: AuditRow['automationTier'];
  origin: AuditRow['origin'];
}

const TierTag = ({ tier, origin }: TierTagProps) => {
  // Only two labels ever reach the user (Harry 2026-09-09). `pending-api` is a build-time marker for
  // us, not a state anyone auditing a client needs to reason about — and every row carrying it emits
  // a real verdict today, so it reads as auto. Collapsing here rather than at the source keeps the
  // catalog's own tier honest for the backend's coverage accounting.
  const isAuto = origin === Origin.AUTO || tier === 'auto' || tier === 'pending-api';
  const label = isAuto ? 'auto' : 'manual';
  const title = isAuto
    ? 'Auto — this verdict was derived automatically from connector data'
    : 'Manual — this verdict is judged by a person';
  return (
    <span className={cn('tier-tag', label)} title={title}>
      {label}
    </span>
  );
};
